import { useEffect, useState, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, useParams, useSearchParams, Navigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from 'react-i18next';
import './i18n';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { QRCodeSVG } from 'qrcode.react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Bell, Map, Gavel, FileCheck, Users, CreditCard, TrendingUp, Globe, ChevronRight, ChevronLeft, Menu, X, Upload, Calculator, Shield, Star, CheckCircle, AlertTriangle, LogIn, LogOut, User, Building2, Edit, Trash2, Plus, Home as HomeIcon, Store, Phone, Mail, MapPin } from 'lucide-react';

// Horse Logo Component - Uses generated image
const HorseLogo = ({ className = "w-6 h-6" }) => (
  <img 
    src="https://static.prod-images.emergentagent.com/jobs/e377de36-ce0f-4123-ad67-01ddf38403dd/images/22b109b6946c14fc6c8806e720fd72f68c77151bfb3b86fcc126d89260a1ad97.png"
    alt="Horse Logo"
    className={className}
  />
);

// Simple Horse Icon for menu items (SVG fallback)
const Horse = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M14 6c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-4 9v6h2v-4h2v4h2v-6c0-1.1-.9-2-2-2h-2c-1.1 0-2 .9-2 2zm8-4c0-1.1-.9-2-2-2h-1V7h-2v2h-1c-1.1 0-2 .9-2 2v1h8v-1z"/>
  </svg>
);

// Italian Regions (all 20)
const ITALIAN_REGIONS = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
  "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto"
];

// Swiss Cantons (all 26)
const SWISS_CANTONS = [
  "Argovia", "Appenzello Esterno", "Appenzello Interno", "Basilea Campagna",
  "Basilea Città", "Berna", "Friburgo", "Ginevra", "Glarona", "Grigioni",
  "Giura", "Lucerna", "Neuchâtel", "Nidvaldo", "Obvaldo", "San Gallo",
  "Sciaffusa", "Soletta", "Svitto", "Ticino", "Turgovia", "Uri",
  "Vallese", "Vaud", "Zugo", "Zurigo"
];

// Get regions based on country
const getRegionsByCountry = (country) => {
  if (country === 'IT') return ITALIAN_REGIONS;
  if (country === 'CH') return SWISS_CANTONS;
  return [...ITALIAN_REGIONS, ...SWISS_CANTONS];
};

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// ===== AUTH CONTEXT =====
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    localStorage.setItem('token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const authAxios = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, authAxios }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Horse className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom marker icons
const goldIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-gold.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const orangeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Language Selector Component
const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  ];

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <div className="relative" data-testid="language-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-none hover:border-primary transition-colors"
        data-testid="language-toggle-btn"
      >
        <Globe className="w-4 h-4 text-primary" />
        <span className="text-sm">{currentLang.flag} {currentLang.name}</span>
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-card border border-border rounded-none shadow-2xl z-50 min-w-[160px]">
          {languages.map(lang => (
            <button
              key={lang.code}
              onClick={() => {
                i18n.changeLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 text-left hover:bg-muted transition-colors flex items-center gap-2 ${
                lang.code === i18n.language ? 'text-primary bg-muted' : ''
              }`}
              data-testid={`lang-${lang.code}`}
            >
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Country Toggle Component
const CountryToggle = ({ selected, onChange }) => {
  return (
    <div className="flex items-center gap-1 bg-card border border-border p-1" data-testid="country-toggle">
      <button
        onClick={() => onChange('IT')}
        className={`px-4 py-2 text-sm font-medium transition-all ${
          selected === 'IT' 
            ? 'bg-primary text-primary-foreground' 
            : 'hover:bg-muted'
        }`}
        data-testid="country-it-btn"
      >
        🇮🇹 Italia
      </button>
      <button
        onClick={() => onChange('CH')}
        className={`px-4 py-2 text-sm font-medium transition-all ${
          selected === 'CH' 
            ? 'bg-primary text-primary-foreground' 
            : 'hover:bg-muted'
        }`}
        data-testid="country-ch-btn"
      >
        🇨🇭 Svizzera
      </button>
      <button
        onClick={() => onChange(null)}
        className={`px-4 py-2 text-sm font-medium transition-all ${
          selected === null 
            ? 'bg-primary text-primary-foreground' 
            : 'hover:bg-muted'
        }`}
        data-testid="country-all-btn"
      >
        Tutti
      </button>
    </div>
  );
};

// Notification Bell Component
const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-muted rounded-none transition-colors"
        data-testid="notification-toggle-btn"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-xs flex items-center justify-center text-white font-bold">
            {unreadCount}
          </span>
        )}
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-card border border-border shadow-2xl z-50 max-h-96 overflow-y-auto" data-testid="notification-dropdown">
          <div className="p-4 border-b border-border">
            <h3 className="font-cinzel font-bold text-primary">Notifiche</h3>
          </div>
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              Nessuna notifica
            </div>
          ) : (
            notifications.slice(0, 10).map(notif => (
              <div
                key={notif.id}
                className={`p-4 border-b border-border hover:bg-muted cursor-pointer transition-colors ${
                  !notif.read ? 'bg-muted/50' : ''
                }`}
                onClick={() => markAsRead(notif.id)}
                data-testid={`notification-item-${notif.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 mt-2 rounded-full ${!notif.read ? 'bg-primary' : 'bg-muted'}`} />
                  <div>
                    <p className="font-medium text-sm">{notif.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{notif.message}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const navItems = [
    { path: '/', label: t('nav.home'), icon: HomeIcon },
    { path: '/horses', label: t('nav.horses'), icon: Horse },
    { path: '/map', label: t('nav.map'), icon: Map },
    { path: '/shops', label: t('nav.shops'), icon: Store },
    { path: '/auctions', label: t('nav.auctions'), icon: Gavel },
    { path: '/certificates', label: t('nav.certificates'), icon: FileCheck },
    { path: '/sponsors', label: t('nav.sponsors'), icon: Users },
    { path: '/subscription', label: t('nav.subscription'), icon: CreditCard },
  ];

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-border" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <HorseLogo className="w-10 h-10" />
            <span className="font-cinzel font-bold text-xl tracking-tight">EquiInvest<span className="text-primary">AI</span></span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`nav-link-${item.path.replace('/', '') || 'home'}`}
              >
                {item.label}
              </Link>
            ))}
            {user && (
              <Link
                to="/dashboard"
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  location.pathname === '/dashboard'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="nav-link-dashboard"
              >
                Dashboard
              </Link>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Mobile Menu Toggle - First on mobile */}
            <button
              className="lg:hidden p-2 hover:bg-muted rounded"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            <div className="hidden sm:block">
              <LanguageSelector />
            </div>
            <NotificationBell />
            
            {/* User Menu */}
            {user ? (
              <div className="relative hidden sm:block">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 bg-card border border-border hover:border-primary transition-colors"
                  data-testid="user-menu-btn"
                >
                  <User className="w-4 h-4 text-primary" />
                  <span className="text-sm hidden md:block">{user.name}</span>
                </button>
                {userMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-card border border-border shadow-2xl z-50 min-w-[200px]">
                    <div className="p-4 border-b border-border">
                      <p className="font-medium">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                      <p className="text-xs text-primary mt-1 capitalize">{user.user_type}</p>
                    </div>
                    <Link
                      to="/dashboard"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 hover:bg-muted transition-colors"
                      data-testid="menu-dashboard"
                    >
                      <Building2 className="w-4 h-4" />
                      Dashboard
                    </Link>
                    <Link
                      to="/dashboard/horses"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 hover:bg-muted transition-colors"
                      data-testid="menu-my-horses"
                    >
                      <Horse className="w-4 h-4" />
                      I Miei Cavalli
                    </Link>
                    <Link
                      to="/dashboard/auctions"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 hover:bg-muted transition-colors"
                      data-testid="menu-my-auctions"
                    >
                      <Gavel className="w-4 h-4" />
                      Le Mie Aste
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted transition-colors text-red-400"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4" />
                      Esci
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
                data-testid="login-btn"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden md:block">Accedi</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border" data-testid="mobile-menu">
          {/* Language Selector in Mobile Menu */}
          <div className="sm:hidden px-6 py-4 border-b border-border">
            <LanguageSelector />
          </div>
          
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-6 py-4 border-b border-border ${
                location.pathname === item.path ? 'text-primary bg-muted' : ''
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
          
          {/* Auth links in mobile menu */}
          {user ? (
            <>
              <Link
                to="/dashboard"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-6 py-4 border-b border-border"
              >
                <Building2 className="w-5 h-5" />
                Dashboard
              </Link>
              <button
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-6 py-4 text-red-400"
              >
                <LogOut className="w-5 h-5" />
                Esci
              </button>
            </>
          ) : (
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-6 py-4 text-primary"
            >
              <LogIn className="w-5 h-5" />
              Accedi
            </Link>
          )}
        </div>
      )}
    </nav>
  );
};

// PWA Install Banner Component
const InstallAppBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already installed or dismissed
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (dismissed) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Show banner on mobile even without beforeinstallprompt
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isMobile && !isStandalone && !dismissed) {
      setTimeout(() => setShowBanner(true), 3000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    } else {
      // Show instructions for iOS
      alert('Per installare l\'app:\n\niPhone/iPad: Tocca il pulsante Condividi e poi "Aggiungi a Home"\n\nAndroid: Tocca il menu ⋮ e poi "Installa app"');
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', 'true');
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-card border border-primary p-4 shadow-lg z-[9998] animate-in slide-in-from-bottom">
      <div className="flex items-start gap-3">
        <HorseLogo className="w-12 h-12 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="font-bold text-sm">Installa EquiInvest AI</h4>
          <p className="text-xs text-muted-foreground mt-1">Accedi velocemente dall'icona sul tuo telefono</p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium hover:opacity-90"
            >
              Installa
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              Non ora
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// App Store Badges Component
const AppStoreBadges = () => {
  return (
    <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
      {/* App Store Badge */}
      <a 
        href="#" 
        onClick={(e) => { e.preventDefault(); alert('App Store: Coming Soon! Per ora puoi installare la PWA dal browser.'); }}
        className="group relative"
      >
        <div className="flex items-center gap-2 px-4 py-2 bg-black rounded-lg border border-gray-700 hover:border-gray-500 transition-colors">
          <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
          <div className="text-left">
            <p className="text-[10px] text-gray-400">Scarica su</p>
            <p className="text-sm font-semibold text-white">App Store</p>
          </div>
          <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-primary text-[10px] font-bold text-black rounded">SOON</span>
        </div>
      </a>

      {/* Google Play Badge */}
      <a 
        href="#" 
        onClick={(e) => { e.preventDefault(); alert('Google Play: Coming Soon! Per ora puoi installare la PWA dal browser.'); }}
        className="group relative"
      >
        <div className="flex items-center gap-2 px-4 py-2 bg-black rounded-lg border border-gray-700 hover:border-gray-500 transition-colors">
          <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
            <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.25-.84-.76-.84-1.35m13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27m3.35-4.31c.34.27.59.69.59 1.19s-.22.9-.57 1.18l-2.29 1.32-2.5-2.5 2.5-2.5 2.27 1.31M6.05 2.66l10.76 6.22-2.27 2.27L6.05 2.66z" fill="#34A853"/>
            <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.25-.84-.76-.84-1.35" fill="#4285F4"/>
            <path d="M16.81 15.12L6.05 21.34l8.49-8.49 2.27 2.27" fill="#FBBC04"/>
            <path d="M6.05 2.66l10.76 6.22-2.27 2.27L6.05 2.66" fill="#EA4335"/>
          </svg>
          <div className="text-left">
            <p className="text-[10px] text-gray-400">Disponibile su</p>
            <p className="text-sm font-semibold text-white">Google Play</p>
          </div>
          <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-primary text-[10px] font-bold text-black rounded">SOON</span>
        </div>
      </a>
    </div>
  );
};

// Home Page Component
const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed database first
        await axios.post(`${API}/seed`);
        
        const [statsRes, horsesRes] = await Promise.all([
          axios.get(`${API}/statistics/market`),
          axios.get(`${API}/horses`)
        ]);
        setStats(statsRes.data);
        setHorses(horsesRes.data.slice(0, 4));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Horse className="w-16 h-16 text-primary animate-pulse mx-auto" />
          <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1704687056866-99e7d65967f0?w=1920')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
        
        <div className="relative z-10 text-center px-6 max-w-4xl">
          <h1 className="font-cinzel text-5xl md:text-7xl font-black tracking-tight mb-6" data-testid="hero-title">
            {t('hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 font-outfit">
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/horses')}
              className="px-8 py-4 bg-primary text-primary-foreground font-medium text-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              data-testid="hero-cta-btn"
            >
              {t('hero.cta')}
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/map')}
              className="px-8 py-4 border border-primary text-primary font-medium text-lg hover:bg-primary/10 transition-colors flex items-center justify-center gap-2"
              data-testid="hero-map-btn"
            >
              <Map className="w-5 h-5" />
              {t('nav.map')}
            </button>
          </div>
          
          {/* Country Flags */}
          <div className="mt-12 flex justify-center gap-8">
            <div className="text-center">
              <span className="text-4xl">🇮🇹</span>
              <p className="text-sm text-muted-foreground mt-2">{t('common.italy')}</p>
            </div>
            <div className="text-center">
              <span className="text-4xl">🇨🇭</span>
              <p className="text-sm text-muted-foreground mt-2">{t('common.switzerland')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-card/30" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-cinzel text-3xl font-bold text-center mb-4">Perché EquiInvest AI?</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">La piattaforma intelligente per investitori equestri in Italia e Svizzera</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card border border-border p-6 text-center hover:border-primary transition-colors">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-cinzel font-bold mb-2">Analisi AI ROI</h3>
              <p className="text-sm text-muted-foreground">Previsioni intelligenti sul ritorno degli investimenti equestri</p>
            </div>
            <div className="bg-card border border-border p-6 text-center hover:border-primary transition-colors">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-cinzel font-bold mb-2">Certificati Blockchain</h3>
              <p className="text-sm text-muted-foreground">Certificati sanitari verificabili su Bitcoin via OpenTimestamps</p>
            </div>
            <div className="bg-card border border-border p-6 text-center hover:border-primary transition-colors">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Map className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-cinzel font-bold mb-2">Eventi FISE/FEI</h3>
              <p className="text-sm text-muted-foreground">Mappa interattiva di tutti gli eventi equestri IT/CH</p>
            </div>
            <div className="bg-card border border-border p-6 text-center hover:border-primary transition-colors">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-cinzel font-bold mb-2">Matching Sponsor</h3>
              <p className="text-sm text-muted-foreground">Trova sponsor cross-border con alert dazi automatici</p>
            </div>
          </div>
        </div>
      </section>

      {/* Regions Section */}
      <section className="py-20 px-6" data-testid="regions-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-cinzel text-3xl font-bold text-center mb-4">Regioni Coperte</h2>
          <p className="text-muted-foreground text-center mb-12">Operiamo in tutte le principali regioni equestri di Italia e Svizzera</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Italy */}
            <div className="bg-card border border-border p-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-4xl">🇮🇹</span>
                <div>
                  <h3 className="font-cinzel text-xl font-bold">Italia</h3>
                  <p className="text-sm text-muted-foreground">20 Regioni</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {['Lombardia', 'Toscana', 'Piemonte', 'Lazio', 'Emilia-Romagna', 'Veneto', 'Umbria', 'Marche', 'Sicilia', 'Sardegna', 'Campania', 'Puglia'].map(region => (
                  <div key={region} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 bg-primary rounded-full"></span>
                    {region}
                  </div>
                ))}
                <div className="flex items-center gap-2 text-sm text-primary">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  + altre 8
                </div>
              </div>
            </div>
            
            {/* Switzerland */}
            <div className="bg-card border border-border p-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-4xl">🇨🇭</span>
                <div>
                  <h3 className="font-cinzel text-xl font-bold">Svizzera</h3>
                  <p className="text-sm text-muted-foreground">26 Cantoni</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {['Ticino', 'Zurigo', 'Berna', 'Ginevra', 'Vaud', 'Lucerna', 'Argovia', 'San Gallo', 'Grigioni', 'Basilea', 'Vallese', 'Friburgo'].map(canton => (
                  <div key={canton} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 bg-accent rounded-full"></span>
                    {canton}
                  </div>
                ))}
                <div className="flex items-center gap-2 text-sm text-accent">
                  <span className="w-2 h-2 bg-accent rounded-full"></span>
                  + altri 14
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - only show when there are horses */}
      {stats && stats.total_horses > 0 && (
        <section className="py-20 px-6" data-testid="stats-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-cinzel text-3xl font-bold text-center mb-12">{t('dashboard.title')}</h2>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-card border border-border p-6" data-testid="stat-total-horses">
                <Horse className="w-8 h-8 text-primary mb-4" />
                <p className="text-3xl font-mono font-bold text-primary">{stats.total_horses}</p>
                <p className="text-sm text-muted-foreground">{t('dashboard.totalHorses')}</p>
              </div>
              <div className="bg-card border border-border p-6" data-testid="stat-avg-roi">
                <TrendingUp className="w-8 h-8 text-accent mb-4" />
                <p className="text-3xl font-mono font-bold text-accent">
                  {((stats.avg_roi_by_country.IT + stats.avg_roi_by_country.CH) / 2).toFixed(1)}%
                </p>
                <p className="text-sm text-muted-foreground">{t('dashboard.avgRoi')}</p>
              </div>
              <div className="bg-card border border-border p-6" data-testid="stat-it-horses">
                <span className="text-3xl">🇮🇹</span>
                <p className="text-3xl font-mono font-bold mt-2">{stats.by_country.IT}</p>
                <p className="text-sm text-muted-foreground">Cavalli Italia</p>
              </div>
              <div className="bg-card border border-border p-6" data-testid="stat-ch-horses">
                <span className="text-3xl">🇨🇭</span>
                <p className="text-3xl font-mono font-bold mt-2">{stats.by_country.CH}</p>
                <p className="text-sm text-muted-foreground">Cavalli Svizzera</p>
              </div>
            </div>

            {/* Market Trend Chart - only when price_trend has data */}
            {stats.price_trend && stats.price_trend.length > 0 && (
              <div className="mt-12 bg-card border border-border p-6" data-testid="market-trend-chart">
                <h3 className="font-cinzel text-xl font-bold mb-6">{t('dashboard.marketTrend')} IT/CH</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats.price_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="month" stroke="#666" />
                    <YAxis stroke="#666" tickFormatter={(v) => `€${v/1000}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2833', border: '1px solid #C5A059' }}
                      formatter={(value) => [`€${value.toLocaleString()}`, '']}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="IT" stroke="#C5A059" strokeWidth={2} name="🇮🇹 Italia" />
                    <Line type="monotone" dataKey="CH" stroke="#CC5500" strokeWidth={2} name="🇨🇭 Svizzera" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Featured Horses - only show when there are horses */}
      {horses.length > 0 && (
        <section className="py-20 px-6 bg-card/50" data-testid="featured-horses-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <h2 className="font-cinzel text-3xl font-bold">Cavalli in Evidenza</h2>
              <Link to="/horses" className="text-primary flex items-center gap-2 hover:underline" data-testid="view-all-horses-link">
                Vedi tutti <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {horses.map(horse => (
                <div 
                  key={horse.id} 
                  className="bg-card border border-border overflow-hidden group hover:border-primary transition-colors"
                  data-testid={`horse-card-${horse.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={horse.images?.[0] || 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=400'} 
                      alt={horse.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span>{horse.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
                      <span className="text-xs text-muted-foreground">{horse.region}</span>
                    </div>
                    <h3 className="font-cinzel font-bold text-lg">{horse.name}</h3>
                    <p className="text-sm text-muted-foreground">{horse.breed} • {horse.age} anni</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="font-mono text-primary font-bold">€{horse.price_eur?.toLocaleString()}</span>
                      <span className="text-sm text-accent font-mono">ROI {horse.roi_prediction?.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Regions Section */}
      <section className="py-20 px-6" data-testid="regions-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-cinzel text-3xl font-bold text-center mb-12">Regioni Coperte</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {['Ticino', 'Lombardia', 'Toscana', 'Piemonte'].map((region, i) => (
              <div 
                key={region}
                className="bg-card border border-border p-6 text-center hover:border-primary transition-colors cursor-pointer"
                onClick={() => navigate(`/horses?region=${region}`)}
                data-testid={`region-card-${region.toLowerCase()}`}
              >
                <span className="text-4xl">{i === 0 ? '🇨🇭' : '🇮🇹'}</span>
                <h3 className="font-cinzel font-bold text-xl mt-4">{region}</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  {stats?.by_region?.[region] || 0} cavalli
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download App Section */}
      <section className="py-20 bg-card border-t border-border">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-cinzel text-3xl md:text-4xl font-bold mb-4">Scarica l'App</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Accedi a EquiInvest AI ovunque tu sia. Gestisci cavalli, eventi e aste dal tuo smartphone.
          </p>
          <AppStoreBadges />
          <p className="text-xs text-muted-foreground mt-6">
            Puoi anche installare l'app direttamente dal browser come PWA
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-background border-t border-border">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <HorseLogo className="w-10 h-10" />
                <span className="font-cinzel font-bold text-xl">EquiInvest<span className="text-primary">AI</span></span>
              </div>
              <p className="text-sm text-muted-foreground">
                Piattaforma AI per investimenti equestri in Italia e Svizzera
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Navigazione</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/horses" className="hover:text-primary">Cavalli</Link></li>
                <li><Link to="/map" className="hover:text-primary">Eventi</Link></li>
                <li><Link to="/auctions" className="hover:text-primary">Aste</Link></li>
                <li><Link to="/sponsors" className="hover:text-primary">Sponsor</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Account</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/login" className="hover:text-primary">Accedi</Link></li>
                <li><Link to="/register" className="hover:text-primary">Registrati</Link></li>
                <li><Link to="/subscription" className="hover:text-primary">Abbonamenti</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Scarica l'App</h4>
              <div className="space-y-2">
                <AppStoreBadges />
              </div>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>© 2026 EquiInvest AI. Tutti i diritti riservati.</p>
            <p className="mt-2">🇮🇹 Italia • 🇨🇭 Svizzera</p>
          </div>
        </div>
      </footer>

      {/* Install App Banner */}
      <InstallAppBanner />
    </div>
  );
};

// Horses Page Component
const HorsesPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country'));
  const [selectedRegion, setSelectedRegion] = useState(searchParams.get('region'));
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const disciplines = [
    { id: 'salto_ostacoli', name: 'Salto Ostacoli', icon: '🏇' },
    { id: 'dressage', name: 'Dressage', icon: '🎩' },
    { id: 'completo', name: 'Completo', icon: '🏆' },
    { id: 'endurance', name: 'Endurance', icon: '🏃' },
    { id: 'reining', name: 'Reining', icon: '🤠' },
    { id: 'attacchi', name: 'Attacchi', icon: '🐎' },
  ];

  const fetchHorses = async () => {
    try {
      let url = `${API}/horses`;
      const params = new URLSearchParams();
      if (selectedCountry) params.append('country', selectedCountry);
      if (selectedRegion) params.append('region', selectedRegion);
      if (selectedDiscipline) params.append('discipline', selectedDiscipline);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setHorses(response.data);
    } catch (error) {
      console.error('Error fetching horses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHorses();
  }, [selectedCountry, selectedRegion, selectedDiscipline]);

  // Get regions based on selected country
  const regions = selectedCountry ? getRegionsByCountry(selectedCountry) : [...ITALIAN_REGIONS, ...SWISS_CANTONS];

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/horses/upload-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert(response.data.message);
      fetchHorses();
    } catch (error) {
      alert('Errore upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Add Horse Form Component
  const AddHorseForm = () => {
    const [formData, setFormData] = useState({
      name: '',
      breed: '',
      age: '',
      region: 'Toscana',
      country: 'IT',
      discipline: 'salto_ostacoli',
      price_eur: '',
      fise_id: '',
      sire: '',
      dam: '',
      competitions: '',
      wins: '',
      image_url: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);

      try {
        const horseData = {
          name: formData.name,
          breed: formData.breed,
          age: parseInt(formData.age),
          region: formData.region,
          country: formData.country,
          discipline: formData.discipline,
          price_eur: parseFloat(formData.price_eur),
          fise_id: formData.fise_id || null,
          pedigree: {
            sire: formData.sire || '',
            dam: formData.dam || ''
          },
          performance_data: {
            competitions: parseInt(formData.competitions) || 0,
            wins: parseInt(formData.wins) || 0
          },
          images: formData.image_url ? [formData.image_url] : []
        };

        await axios.post(`${API}/horses`, horseData);
        alert('Cavallo aggiunto con successo!');
        setShowAddForm(false);
        fetchHorses();
      } catch (error) {
        alert('Errore: ' + (error.response?.data?.detail || error.message));
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="add-horse-modal">
        <div className="bg-card border border-border p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-cinzel text-2xl font-bold">Aggiungi Cavallo</h2>
            <button onClick={() => setShowAddForm(false)} className="p-2 hover:bg-muted" data-testid="close-add-form">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                  placeholder="es. Tosco del Chianti"
                  data-testid="horse-name-input"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Razza *</label>
                <input
                  type="text"
                  required
                  value={formData.breed}
                  onChange={(e) => setFormData({...formData, breed: e.target.value})}
                  className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                  placeholder="es. Maremmano"
                  data-testid="horse-breed-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Età (anni) *</label>
                <input
                  type="number"
                  required
                  min="1"
                  max="30"
                  value={formData.age}
                  onChange={(e) => setFormData({...formData, age: e.target.value})}
                  className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                  data-testid="horse-age-input"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Paese *</label>
                <select
                  value={formData.country}
                  onChange={(e) => {
                    const newCountry = e.target.value;
                    const newRegions = getRegionsByCountry(newCountry);
                    setFormData({...formData, country: newCountry, region: newRegions[0]});
                  }}
                  className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                  data-testid="horse-country-select"
                >
                  <option value="IT">🇮🇹 Italia</option>
                  <option value="CH">🇨🇭 Svizzera</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Regione *</label>
                <select
                  value={formData.region}
                  onChange={(e) => setFormData({...formData, region: e.target.value})}
                  className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                  data-testid="horse-region-select"
                >
                  {regions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-muted-foreground mb-2">Disciplina *</label>
              <select
                value={formData.discipline}
                onChange={(e) => setFormData({...formData, discipline: e.target.value})}
                className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                data-testid="horse-discipline-select"
              >
                {disciplines.map(d => (
                  <option key={d.id} value={d.id}>{d.icon} {d.name}</option>
                ))}
              </select>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Prezzo (€) *</label>
                <input
                  type="number"
                  required
                  min="1000"
                  value={formData.price_eur}
                  onChange={(e) => setFormData({...formData, price_eur: e.target.value})}
                  className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                  placeholder="es. 45000"
                  data-testid="horse-price-input"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">FISE ID</label>
                <input
                  type="text"
                  value={formData.fise_id}
                  onChange={(e) => setFormData({...formData, fise_id: e.target.value})}
                  className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                  placeholder="es. IT-FIS-2020-1234"
                  data-testid="horse-fise-input"
                />
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="font-cinzel font-bold mb-4">Pedigree</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Padre</label>
                  <input
                    type="text"
                    value={formData.sire}
                    onChange={(e) => setFormData({...formData, sire: e.target.value})}
                    className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                    placeholder="Nome padre"
                    data-testid="horse-sire-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Madre</label>
                  <input
                    type="text"
                    value={formData.dam}
                    onChange={(e) => setFormData({...formData, dam: e.target.value})}
                    className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                    placeholder="Nome madre"
                    data-testid="horse-dam-input"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="font-cinzel font-bold mb-4">Performance</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Competizioni</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.competitions}
                    onChange={(e) => setFormData({...formData, competitions: e.target.value})}
                    className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                    data-testid="horse-competitions-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Vittorie</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.wins}
                    onChange={(e) => setFormData({...formData, wins: e.target.value})}
                    className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                    data-testid="horse-wins-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm text-muted-foreground mb-2">URL Immagine</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                placeholder="https://..."
                data-testid="horse-image-input"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="flex-1 px-4 py-3 border border-border text-foreground hover:bg-muted transition-colors"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="submit-horse-btn"
              >
                {submitting ? 'Salvando...' : 'Aggiungi Cavallo'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pt-20 px-6" data-testid="horses-page">
      <div className="max-w-7xl mx-auto py-12">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
          <h1 className="font-cinzel text-4xl font-bold">{t('horses.title')}</h1>
          
          <div className="flex flex-wrap items-center gap-4">
            <CountryToggle selected={selectedCountry} onChange={setSelectedCountry} />
            
            <select
              value={selectedRegion || ''}
              onChange={(e) => setSelectedRegion(e.target.value || null)}
              className="px-4 py-2 bg-card border border-border text-foreground"
              data-testid="region-filter"
            >
              <option value="">{t('horses.filter.all')} Regioni</option>
              {regions.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Discipline Filter Buttons */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="discipline-buttons">
          <button
            onClick={() => setSelectedDiscipline(null)}
            className={`px-4 py-2 text-sm font-medium transition-all ${
              !selectedDiscipline 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-card border border-border hover:border-primary'
            }`}
          >
            Tutte
          </button>
          {disciplines.map(d => (
            <button
              key={d.id}
              onClick={() => setSelectedDiscipline(d.id)}
              className={`px-4 py-2 text-sm font-medium transition-all flex items-center gap-2 ${
                selectedDiscipline === d.id 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-card border border-border hover:border-primary'
              }`}
              data-testid={`discipline-btn-${d.id}`}
            >
              <span>{d.icon}</span>
              <span>{d.name}</span>
            </button>
          ))}
        </div>

        {/* Add Horse Section */}
        <div className="bg-card border border-border p-6 mb-8" data-testid="add-horse-section">
          <h2 className="font-cinzel text-xl font-bold mb-4">Aggiungi Cavalli</h2>
          <div className="flex flex-col md:flex-row gap-4">
            <button
              onClick={() => setShowAddForm(true)}
              className="flex-1 px-6 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              data-testid="add-horse-btn"
            >
              <Star className="w-5 h-5" />
              Inserimento Manuale
            </button>
            <label className="flex-1 px-6 py-3 border border-primary text-primary font-medium hover:bg-primary/10 transition-colors flex items-center justify-center gap-2 cursor-pointer">
              <Upload className="w-5 h-5" />
              {uploading ? 'Caricamento...' : 'Upload CSV/JSON'}
              <input
                type="file"
                accept=".csv,.json"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
                data-testid="upload-file-input"
              />
            </label>
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            Formato CSV: name, breed, age, region, country, price_eur, fise_id
          </p>
        </div>

        {showAddForm && <AddHorseForm />}

        {loading ? (
          <div className="text-center py-20">
            <Horse className="w-12 h-12 text-primary animate-pulse mx-auto" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {horses.map(horse => (
              <div 
                key={horse.id}
                className="bg-card border border-border overflow-hidden group hover:border-primary transition-all duration-300"
                data-testid={`horse-list-item-${horse.id}`}
              >
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img 
                    src={horse.images?.[0] || 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=600'} 
                    alt={horse.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="bg-black/60 backdrop-blur px-3 py-1 text-sm">
                      {horse.country === 'IT' ? '🇮🇹' : '🇨🇭'} {horse.region}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4 bg-accent px-3 py-1">
                    <span className="font-mono text-sm font-bold text-white">ROI {horse.roi_prediction?.toFixed(1)}%</span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-cinzel font-bold text-xl">{horse.name}</h3>
                    <span className="text-lg" title={disciplines.find(d => d.id === horse.discipline)?.name}>
                      {disciplines.find(d => d.id === horse.discipline)?.icon || '🐴'}
                    </span>
                  </div>
                  <p className="text-muted-foreground">{horse.breed} • {horse.age} {t('horses.years')}</p>
                  <p className="text-xs text-primary mt-1">
                    {disciplines.find(d => d.id === horse.discipline)?.name || 'Salto Ostacoli'}
                  </p>
                  
                  {horse.fise_id && (
                    <p className="text-xs text-muted-foreground mt-2 font-mono">FISE: {horse.fise_id}</p>
                  )}
                  
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-mono text-2xl text-primary font-bold">€{horse.price_eur?.toLocaleString()}</span>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      onClick={() => navigate(`/horses/${horse.id}`)}
                      className="px-4 py-2 bg-muted text-foreground text-sm hover:bg-muted/80 transition-colors"
                      data-testid={`view-horse-${horse.id}`}
                    >
                      {t('horses.viewDetails')}
                    </button>
                    <button
                      onClick={() => navigate(`/horses/${horse.id}?analyze=true`)}
                      className="px-4 py-2 bg-primary text-primary-foreground text-sm hover:opacity-90 transition-opacity"
                      data-testid={`analyze-horse-${horse.id}`}
                    >
                      {t('horses.analyzeRoi')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Horse Detail Page Component
const HorseDetailPage = () => {
  const { t } = useTranslation();
  const { horseId } = useParams();
  const [searchParams] = useSearchParams();
  const [horse, setHorse] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHorse = async () => {
      try {
        const response = await axios.get(`${API}/horses/${horseId}`);
        setHorse(response.data);
        
        if (searchParams.get('analyze') === 'true') {
          runAnalysis();
        }
      } catch (error) {
        console.error('Error fetching horse:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHorse();
  }, [horseId, searchParams]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/ai/roi-analysis`, { horse_id: horseId });
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error running analysis:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Horse className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  if (!horse) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Cavallo non trovato</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 px-6" data-testid="horse-detail-page">
      <div className="max-w-6xl mx-auto py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image */}
          <div>
            <div className="aspect-[4/3] overflow-hidden border border-border">
              <img 
                src={horse.images?.[0] || 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=800'} 
                alt={horse.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">{horse.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
              <span className="text-muted-foreground">{horse.region}</span>
            </div>
            
            <h1 className="font-cinzel text-4xl font-bold mb-4" data-testid="horse-name">{horse.name}</h1>
            
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-3xl text-primary font-bold" data-testid="horse-price">
                €{horse.price_eur?.toLocaleString()}
              </span>
              <span className="bg-accent px-4 py-2 font-mono text-white" data-testid="horse-roi">
                ROI {horse.roi_prediction?.toFixed(1)}%
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-card border border-border p-4">
                <p className="text-sm text-muted-foreground">{t('horses.breed')}</p>
                <p className="font-medium">{horse.breed}</p>
              </div>
              <div className="bg-card border border-border p-4">
                <p className="text-sm text-muted-foreground">{t('horses.age')}</p>
                <p className="font-medium">{horse.age} {t('horses.years')}</p>
              </div>
            </div>

            {horse.fise_id && (
              <div className="bg-card border border-border p-4 mb-8">
                <p className="text-sm text-muted-foreground">FISE ID</p>
                <p className="font-mono">{horse.fise_id}</p>
              </div>
            )}

            <button
              onClick={runAnalysis}
              disabled={analyzing}
              className="w-full px-6 py-4 bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="run-analysis-btn"
            >
              {analyzing ? (
                <>Analisi in corso...</>
              ) : (
                <>
                  <TrendingUp className="w-5 h-5" />
                  {t('horses.analyzeRoi')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Pedigree & Performance */}
        <div className="grid lg:grid-cols-2 gap-8 mt-12">
          {/* Pedigree */}
          <div className="bg-card border border-border p-6" data-testid="pedigree-section">
            <h2 className="font-cinzel text-xl font-bold mb-6">{t('horses.pedigree')}</h2>
            <div className="space-y-4">
              {horse.pedigree?.sire && (
                <div>
                  <p className="text-sm text-muted-foreground">Padre</p>
                  <p className="font-medium">{horse.pedigree.sire}</p>
                </div>
              )}
              {horse.pedigree?.dam && (
                <div>
                  <p className="text-sm text-muted-foreground">Madre</p>
                  <p className="font-medium">{horse.pedigree.dam}</p>
                </div>
              )}
              {horse.pedigree?.lineage && (
                <div>
                  <p className="text-sm text-muted-foreground">Linea</p>
                  <p className="font-medium">{horse.pedigree.lineage}</p>
                </div>
              )}
            </div>
          </div>

          {/* Performance */}
          <div className="bg-card border border-border p-6" data-testid="performance-section">
            <h2 className="font-cinzel text-xl font-bold mb-6">{t('horses.performance')}</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('horses.competitions')}</p>
                <p className="text-2xl font-mono font-bold">{horse.performance_data?.competitions || 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('horses.wins')}</p>
                <p className="text-2xl font-mono font-bold text-primary">{horse.performance_data?.wins || 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('horses.places')}</p>
                <p className="text-2xl font-mono font-bold">{horse.performance_data?.places || 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('horses.ranking')}</p>
                <p className="text-2xl font-mono font-bold">#{horse.performance_data?.fise_ranking || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ANAFI Stats */}
        {horse.anafi_stats && (
          <div className="bg-card border border-border p-6 mt-8" data-testid="anafi-section">
            <h2 className="font-cinzel text-xl font-bold mb-6">{t('horses.anafi')}</h2>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-muted-foreground">{t('horses.fertility')}</p>
                <p className="text-3xl font-mono font-bold text-primary">{(horse.anafi_stats.fertility_index * 100).toFixed(0)}%</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('horses.geneticScore')}</p>
                <p className="text-3xl font-mono font-bold text-accent">{horse.anafi_stats.genetic_score}</p>
              </div>
            </div>
          </div>
        )}

        {/* AI Analysis - Enhanced ROI Dashboard */}
        {analysis && (
          <div className="bg-card border border-primary p-6 mt-8" data-testid="ai-analysis-section">
            <div className="flex items-center gap-3 mb-6">
              <Star className="w-6 h-6 text-primary" />
              <h2 className="font-cinzel text-xl font-bold">Analisi ROI Investimento</h2>
              <span className={`ml-auto px-3 py-1 text-xs font-bold uppercase ${
                analysis.risk_level === 'basso' ? 'bg-green-500/20 text-green-400' :
                analysis.risk_level === 'medio' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-red-500/20 text-red-400'
              }`} data-testid="risk-badge">
                Rischio {analysis.risk_level}
              </span>
            </div>

            {/* Top KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              <div className="bg-muted p-4 text-center" data-testid="roi-percent">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">ROI Annuo</p>
                <p className={`text-3xl font-mono font-bold ${analysis.roi_prediction_percent > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {analysis.roi_prediction_percent > 0 ? '+' : ''}{analysis.roi_prediction_percent}%
                </p>
              </div>
              <div className="bg-muted p-4 text-center" data-testid="total-score">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Score</p>
                <p className="text-3xl font-mono font-bold text-primary">{analysis.total_score}<span className="text-lg text-muted-foreground">/{analysis.max_score}</span></p>
              </div>
              <div className="bg-muted p-4 text-center" data-testid="value-3y">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Valore 3 Anni</p>
                <p className="text-2xl font-mono font-bold">EUR {analysis.estimated_value_3y?.toLocaleString()}</p>
              </div>
              <div className="bg-muted p-4 text-center" data-testid="value-5y">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Valore 5 Anni</p>
                <p className="text-2xl font-mono font-bold">EUR {analysis.estimated_value_5y?.toLocaleString()}</p>
              </div>
              <div className="bg-muted p-4 text-center" data-testid="confidence">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Confidenza</p>
                <p className={`text-2xl font-bold capitalize ${
                  analysis.confidence === 'alta' ? 'text-green-400' :
                  analysis.confidence === 'media' ? 'text-yellow-400' : 'text-red-400'
                }`}>{analysis.confidence}</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 mb-8">
              {/* Breakdown Scores */}
              <div data-testid="breakdown-section">
                <h3 className="font-cinzel font-bold mb-4">Breakdown Score</h3>
                <div className="space-y-3">
                  {analysis.breakdown && Object.entries(analysis.breakdown).map(([key, val]) => (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm">{val.label}</span>
                        <span className="text-sm font-mono text-primary">{val.score}/{val.max}</span>
                      </div>
                      <div className="h-2 bg-muted overflow-hidden">
                        <div 
                          className="h-full transition-all duration-700"
                          style={{ 
                            width: `${(val.score / val.max) * 100}%`,
                            backgroundColor: (val.score / val.max) > 0.7 ? '#22c55e' : (val.score / val.max) > 0.4 ? '#c9a227' : '#ef4444'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Value Projection Chart */}
              <div data-testid="projection-chart">
                <h3 className="font-cinzel font-bold mb-4">Proiezione Valore (5 Anni)</h3>
                {analysis.projections && analysis.projections.length > 0 && (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={[
                      { label: 'Oggi', value: analysis.price_eur, roi_cumulative: 0 },
                      ...analysis.projections
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="label" stroke="#666" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#666" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1F2833', border: '1px solid #C5A059', fontSize: 12 }}
                        formatter={(value, name) => [
                          name === 'value' ? `EUR ${value.toLocaleString()}` : `${value}%`,
                          name === 'value' ? 'Valore' : 'ROI Cumulativo'
                        ]}
                      />
                      <Line type="monotone" dataKey="value" stroke="#C5A059" strokeWidth={2} dot={{ fill: '#C5A059', r: 4 }} name="value" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* AI Narrative */}
            <div data-testid="ai-narrative">
              <h3 className="font-cinzel font-bold mb-4">Analisi AI (GPT-4o)</h3>
              <div className="bg-muted p-5 text-sm leading-relaxed whitespace-pre-wrap">
                {analysis.ai_analysis}
              </div>
            </div>

            <p className="text-xs text-muted-foreground mt-4 text-center">
              Analisi generata da EquiInvest AI. I rendimenti passati non garantiscono risultati futuri. Consultare un esperto prima di investire.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Map Page Component
const MapPage = () => {
  const { t } = useTranslation();
  const { user, authAxios } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [showLiveOnly, setShowLiveOnly] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showRankings, setShowRankings] = useState(false);
  const [showEventsList, setShowEventsList] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const navigate = useNavigate();

  const disciplines = [
    { id: 'salto_ostacoli', name: 'Salto Ostacoli', icon: '🏇' },
    { id: 'dressage', name: 'Dressage', icon: '🎩' },
    { id: 'completo', name: 'Completo', icon: '🏆' },
    { id: 'endurance', name: 'Endurance', icon: '🏃' },
    { id: 'reining', name: 'Reining', icon: '🤠' },
    { id: 'attacchi', name: 'Attacchi', icon: '🐎' },
  ];

  const fetchEvents = async () => {
    try {
      let url = `${API}/events`;
      const params = new URLSearchParams();
      if (selectedType) params.append('event_type', selectedType);
      if (selectedDiscipline) params.append('discipline', selectedDiscipline);
      if (showLiveOnly) params.append('is_live', 'true');
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [selectedType, selectedDiscipline, showLiveOnly]);

  // Center of IT/CH region
  const center = [45.5, 9.5];

  // Add Event Form Component
  const AddEventForm = () => {
    const [formData, setFormData] = useState({
      name: '',
      event_type: 'FISE',
      discipline: 'salto_ostacoli',
      region: 'Toscana',
      country: 'IT',
      lat: 45.5,
      lng: 9.5,
      date: '',
      description: '',
      location_name: ''
    });
    const [submitting, setSubmitting] = useState(false);

    // Get regions based on selected country in form
    const regions = getRegionsByCountry(formData.country);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      try {
        await authAxios.post('/events', formData);
        alert('Evento creato con successo!');
        setShowAddEvent(false);
        fetchEvents();
      } catch (error) {
        alert('Errore: ' + (error.response?.data?.detail || error.message));
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
        <div className="bg-card border border-border p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-cinzel text-xl font-bold">Aggiungi Evento</h2>
            <button onClick={() => setShowAddEvent(false)} className="p-2 hover:bg-muted">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground block mb-1">Nome Evento *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
                placeholder="es. Concorso Nazionale Salto"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Tipo</label>
                <select
                  value={formData.event_type}
                  onChange={(e) => setFormData({...formData, event_type: e.target.value})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                >
                  <option value="FISE">FISE</option>
                  <option value="FEI">FEI</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Disciplina</label>
                <select
                  value={formData.discipline}
                  onChange={(e) => setFormData({...formData, discipline: e.target.value})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                >
                  {disciplines.map(d => (
                    <option key={d.id} value={d.id}>{d.icon} {d.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Paese</label>
                <select
                  value={formData.country}
                  onChange={(e) => {
                    const newCountry = e.target.value;
                    const newRegions = getRegionsByCountry(newCountry);
                    setFormData({...formData, country: newCountry, region: newRegions[0]});
                  }}
                  className="w-full px-3 py-2 bg-muted border border-border"
                >
                  <option value="IT">🇮🇹 Italia</option>
                  <option value="CH">🇨🇭 Svizzera</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Regione</label>
                <select
                  value={formData.region}
                  onChange={(e) => setFormData({...formData, region: e.target.value})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                >
                  {regions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Data *</label>
              <input
                type="date"
                required
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Luogo</label>
              <input
                type="text"
                value={formData.location_name}
                onChange={(e) => setFormData({...formData, location_name: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
                placeholder="es. Centro Ippico Firenze"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Latitudine</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.lat}
                  onChange={(e) => setFormData({...formData, lat: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Longitudine</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.lng}
                  onChange={(e) => setFormData({...formData, lng: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Descrizione</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border h-20"
                placeholder="Descrizione dell'evento..."
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowAddEvent(false)}
                className="flex-1 py-3 border border-border text-foreground hover:bg-muted transition-colors"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Creazione...' : 'Crea Evento'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Rankings Modal Component
  const RankingsModal = ({ event, onClose }) => {
    const [rankings, setRankings] = useState(event.rankings || []);
    const [newEntry, setNewEntry] = useState({
      position: rankings.length + 1,
      rider_name: '',
      horse_name: '',
      score: '',
      time: '',
      penalties: 0,
      country: 'IT'
    });
    const [saving, setSaving] = useState(false);

    const addRanking = () => {
      if (!newEntry.rider_name || !newEntry.horse_name) {
        alert('Inserisci nome cavaliere e cavallo');
        return;
      }
      setRankings([...rankings, newEntry]);
      setNewEntry({
        position: rankings.length + 2,
        rider_name: '',
        horse_name: '',
        score: '',
        time: '',
        penalties: 0,
        country: 'IT'
      });
    };

    const removeRanking = (index) => {
      const updated = rankings.filter((_, i) => i !== index);
      // Update positions
      updated.forEach((r, i) => r.position = i + 1);
      setRankings(updated);
    };

    const saveRankings = async () => {
      setSaving(true);
      try {
        await authAxios.post(`/events/${event.id}/rankings`, {
          event_id: event.id,
          rankings: rankings
        });
        alert('Classifica salvata!');
        fetchEvents();
        onClose();
      } catch (error) {
        alert('Errore: ' + (error.response?.data?.detail || error.message));
      } finally {
        setSaving(false);
      }
    };

    const toggleLive = async () => {
      try {
        await authAxios.put(`/events/${event.id}`, {
          is_live: !event.is_live
        });
        fetchEvents();
      } catch (error) {
        alert('Errore: ' + (error.response?.data?.detail || error.message));
      }
    };

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
        <div className="bg-card border border-border p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-cinzel text-xl font-bold">{event.name}</h2>
              <p className="text-sm text-muted-foreground">{disciplines.find(d => d.id === event.discipline)?.name || event.discipline}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleLive}
                className={`px-3 py-1 text-sm font-medium ${event.is_live ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}
              >
                {event.is_live ? '🔴 LIVE - Termina' : '▶️ Inizia Live'}
              </button>
              <button onClick={onClose} className="p-2 hover:bg-muted">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Current Rankings */}
          <div className="mb-6">
            <h3 className="font-bold mb-3">Classifica Attuale</h3>
            {rankings.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nessuna classifica ancora</p>
            ) : (
              <div className="space-y-2">
                {rankings.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-muted">
                    <span className="w-8 text-center font-bold text-primary">{r.position}°</span>
                    <span className="flex-1">{r.rider_name} - {r.horse_name}</span>
                    <span className="text-sm">{r.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
                    {r.score && <span className="text-sm font-mono">{r.score}</span>}
                    {r.time && <span className="text-sm font-mono">{r.time}</span>}
                    {user && (
                      <button onClick={() => removeRanking(i)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add New Entry (only for logged in users) */}
          {user && (
            <div className="border-t border-border pt-4">
              <h3 className="font-bold mb-3">Aggiungi Risultato</h3>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Nome Cavaliere"
                  value={newEntry.rider_name}
                  onChange={(e) => setNewEntry({...newEntry, rider_name: e.target.value})}
                  className="px-3 py-2 bg-muted border border-border text-sm"
                />
                <input
                  type="text"
                  placeholder="Nome Cavallo"
                  value={newEntry.horse_name}
                  onChange={(e) => setNewEntry({...newEntry, horse_name: e.target.value})}
                  className="px-3 py-2 bg-muted border border-border text-sm"
                />
                <input
                  type="text"
                  placeholder="Punteggio (es. 72.5%)"
                  value={newEntry.score}
                  onChange={(e) => setNewEntry({...newEntry, score: e.target.value})}
                  className="px-3 py-2 bg-muted border border-border text-sm"
                />
                <input
                  type="text"
                  placeholder="Tempo (es. 45.32)"
                  value={newEntry.time}
                  onChange={(e) => setNewEntry({...newEntry, time: e.target.value})}
                  className="px-3 py-2 bg-muted border border-border text-sm"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={addRanking}
                  className="flex-1 py-2 bg-muted hover:bg-muted/80 text-sm"
                >
                  + Aggiungi alla Classifica
                </button>
                <button
                  onClick={saveRankings}
                  disabled={saving}
                  className="flex-1 py-2 bg-primary text-primary-foreground text-sm disabled:opacity-50"
                >
                  {saving ? 'Salvataggio...' : 'Salva Classifica'}
                </button>
              </div>
            </div>
          )}

          {/* Close Button */}
          <div className="mt-6 pt-4 border-t border-border">
            <button
              onClick={onClose}
              className="w-full py-2 border border-border text-foreground hover:bg-muted transition-colors"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="map-page">
      <div className="h-[calc(100vh-5rem)] relative">
        {/* Filters Panel - Positioned to avoid zoom controls */}
        {showFilters ? (
          <div className="absolute top-4 left-14 z-[1000] bg-card border border-border p-4 max-w-xs" data-testid="map-filters">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-cinzel font-bold">{t('map.events')}</h3>
              <button 
                onClick={() => setShowFilters(false)}
                className="p-1 hover:bg-muted rounded"
                title="Nascondi filtri"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Type Filter */}
            <div className="flex gap-2 mb-3">
            <button
              onClick={() => setSelectedType(null)}
              className={`px-3 py-1.5 text-xs ${!selectedType ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
            >
              Tutti
            </button>
            <button
              onClick={() => setSelectedType('FISE')}
              className={`px-3 py-1.5 text-xs ${selectedType === 'FISE' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
            >
              FISE
            </button>
            <button
              onClick={() => setSelectedType('FEI')}
              className={`px-3 py-1.5 text-xs ${selectedType === 'FEI' ? 'bg-accent text-white' : 'bg-muted'}`}
            >
              FEI
            </button>
          </div>

          {/* Discipline Filter */}
          <div className="mb-3">
            <label className="text-xs text-muted-foreground block mb-2">Disciplina</label>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setSelectedDiscipline(null)}
                className={`px-2 py-1 text-xs transition-all ${
                  !selectedDiscipline ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'
                }`}
              >
                Tutte
              </button>
              {disciplines.map(d => (
                <button
                  key={d.id}
                  onClick={() => setSelectedDiscipline(d.id)}
                  className={`px-2 py-1 text-xs transition-all flex items-center gap-1 ${
                    selectedDiscipline === d.id ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  <span>{d.icon}</span>
                  <span>{d.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Live Filter */}
          <label className="flex items-center gap-2 cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={showLiveOnly}
              onChange={(e) => setShowLiveOnly(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm">🔴 Solo eventi LIVE</span>
          </label>

          {/* Add Event Button */}
          {user ? (
            <button
              onClick={() => setShowAddEvent(true)}
              className="w-full py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 flex items-center justify-center gap-2 mt-2"
            >
              <Plus className="w-5 h-5" />
              Aggiungi Evento
            </button>
          ) : (
            <div className="mt-2 space-y-2">
              <Link
                to="/login"
                className="w-full py-2 bg-muted text-foreground text-sm text-center block hover:bg-muted/80 border border-border"
              >
                Accedi per aggiungere eventi
              </Link>
              <Link
                to="/register"
                className="w-full py-2 bg-primary text-primary-foreground text-sm text-center block hover:opacity-90"
              >
                Registrati per aggiungere eventi
              </Link>
            </div>
          )}
        </div>
        ) : (
          <div className="absolute top-4 left-14 z-[1000] flex gap-2">
            <button
              onClick={() => setShowFilters(true)}
              className="bg-card border border-border p-3 hover:bg-muted transition-colors"
              title="Mostra filtri"
            >
              <span className="text-sm font-medium">⚙️ Filtri</span>
            </button>
            {user && (
              <button
                onClick={() => setShowAddEvent(true)}
                className="bg-primary text-primary-foreground p-3 hover:opacity-90 transition-opacity flex items-center gap-2"
                title="Aggiungi evento"
              >
                <Plus className="w-5 h-5" />
                <span className="text-sm font-medium">Nuovo Evento</span>
              </button>
            )}
          </div>
        )}

        {/* Map */}
        <MapContainer
          center={center}
          zoom={7}
          style={{ height: '100%', width: '100%' }}
          className="z-0"
        >
          <TileLayer
            attribution='&copy; <a href="https://carto.com/">CartoDB</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          {events.map(event => (
            <Marker
              key={event.id}
              position={[event.lat, event.lng]}
              icon={event.event_type === 'FISE' ? goldIcon : orangeIcon}
            >
              <Popup>
                <div className="p-2 min-w-[220px]">
                  <div className="flex items-center gap-2 mb-2">
                    <span>{event.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
                    <span className={`text-xs px-2 py-1 ${event.event_type === 'FISE' ? 'bg-[#C5A059]' : 'bg-[#CC5500]'} text-white`}>
                      {event.event_type}
                    </span>
                    {event.is_live && (
                      <span className="text-xs px-2 py-1 bg-red-500 text-white animate-pulse">🔴 LIVE</span>
                    )}
                  </div>
                  <h4 className="font-bold text-gray-900">{event.name}</h4>
                  <p className="text-sm text-gray-600">
                    {disciplines.find(d => d.id === event.discipline)?.icon} {disciplines.find(d => d.id === event.discipline)?.name}
                  </p>
                  <p className="text-sm text-gray-600">{event.region} • {event.date}</p>
                  <p className="text-xs text-gray-500 mt-2">{event.description}</p>
                  {event.rankings?.length > 0 && (
                    <p className="text-xs text-blue-600 mt-2">📊 {event.rankings.length} risultati</p>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Events List Sidebar */}
        {showEventsList ? (
          <div className="absolute top-4 right-4 z-[1000] bg-card border border-border p-4 max-h-[80vh] overflow-y-auto w-80" data-testid="events-list">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-cinzel font-bold">{t('map.events')} ({events.length})</h3>
              <button 
                onClick={() => setShowEventsList(false)}
                className="p-1 hover:bg-muted rounded"
                title="Nascondi elenco"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              {events.map(event => (
                <div 
                  key={event.id}
                  onClick={() => { setSelectedEvent(event); setShowRankings(true); }}
                  className={`p-3 border transition-colors cursor-pointer ${
                    event.is_live 
                      ? 'bg-red-500/10 border-red-500 hover:border-red-400' 
                      : 'bg-muted border-border hover:border-primary'
                  }`}
                  data-testid={`event-item-${event.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span>{event.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
                    <span className={`text-xs px-2 py-0.5 ${event.event_type === 'FISE' ? 'bg-primary' : 'bg-accent'} text-white`}>
                      {event.event_type}
                    </span>
                    <span className="text-xs">
                      {disciplines.find(d => d.id === event.discipline)?.icon}
                    </span>
                    {event.is_live && (
                      <span className="text-xs px-2 py-0.5 bg-red-500 text-white animate-pulse ml-auto">LIVE</span>
                    )}
                  </div>
                  <p className="font-medium text-sm">{event.name}</p>
                  <p className="text-xs text-muted-foreground">{event.region} • {event.date}</p>
                  {event.rankings?.length > 0 && (
                    <p className="text-xs text-primary mt-1">📊 Vedi classifica ({event.rankings.length})</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowEventsList(true)}
            className="absolute top-4 right-4 z-[1000] bg-card border border-border p-3 hover:bg-muted transition-colors"
            title="Mostra elenco eventi"
          >
            <span className="text-sm font-medium">📋 Eventi ({events.length})</span>
          </button>
        )}

        {/* Modals */}
        {showAddEvent && <AddEventForm />}
        {showRankings && selectedEvent && (
          <RankingsModal event={selectedEvent} onClose={() => { setShowRankings(false); setSelectedEvent(null); }} />
        )}
      </div>
    </div>
  );
};

// Shops Page Component
const ShopsPage = () => {
  const { t } = useTranslation();
  const { user, authAxios } = useAuth();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showAddShop, setShowAddShop] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [showShopsList, setShowShopsList] = useState(true);

  const categories = [
    { id: 'selleria', name: 'Selleria', icon: '🏇' },
    { id: 'mangimi', name: 'Mangimi', icon: '🌾' },
    { id: 'veterinario', name: 'Veterinario', icon: '⚕️' },
    { id: 'maniscalco', name: 'Maniscalco', icon: '🔨' },
    { id: 'trasporto', name: 'Trasporto', icon: '🚛' },
    { id: 'abbigliamento', name: 'Abbigliamento', icon: '👕' },
    { id: 'attrezzature', name: 'Attrezzature', icon: '🛠️' },
    { id: 'altro', name: 'Altro', icon: '📦' },
  ];

  const fetchShops = async () => {
    try {
      let url = `${API}/shops`;
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedCountry) params.append('country', selectedCountry);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setShops(response.data);
    } catch (error) {
      console.error('Error fetching shops:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
  }, [selectedCategory, selectedCountry]);

  const center = [45.5, 9.5];

  // Add Shop Form Component
  const AddShopForm = () => {
    const [formData, setFormData] = useState({
      name: '',
      category: 'selleria',
      description: '',
      address: '',
      city: '',
      region: 'Toscana',
      country: 'IT',
      lat: 45.5,
      lng: 9.5,
      phone: '',
      email: '',
      website: '',
      opening_hours: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const regions = getRegionsByCountry(formData.country);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      try {
        await authAxios.post('/shops', formData);
        alert('Negozio aggiunto con successo!');
        setShowAddShop(false);
        fetchShops();
      } catch (error) {
        alert('Errore: ' + (error.response?.data?.detail || error.message));
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
        <div className="bg-card border border-border p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-cinzel text-xl font-bold">Aggiungi Negozio</h2>
            <button onClick={() => setShowAddShop(false)} className="p-2 hover:bg-muted">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground block mb-1">Nome Negozio *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
                placeholder="es. Selleria Rossi"
              />
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground block mb-1">Categoria</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
              >
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Paese</label>
                <select
                  value={formData.country}
                  onChange={(e) => {
                    const newCountry = e.target.value;
                    const newRegions = getRegionsByCountry(newCountry);
                    setFormData({...formData, country: newCountry, region: newRegions[0]});
                  }}
                  className="w-full px-3 py-2 bg-muted border border-border"
                >
                  <option value="IT">🇮🇹 Italia</option>
                  <option value="CH">🇨🇭 Svizzera</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Regione</label>
                <select
                  value={formData.region}
                  onChange={(e) => setFormData({...formData, region: e.target.value})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                >
                  {regions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Città *</label>
              <input
                type="text"
                required
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
                placeholder="es. Firenze"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Indirizzo *</label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
                placeholder="Via Roma, 15"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Latitudine</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.lat}
                  onChange={(e) => setFormData({...formData, lat: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Longitudine</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.lng}
                  onChange={(e) => setFormData({...formData, lng: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Telefono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                  placeholder="+39 055 123456"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                  placeholder="info@negozio.it"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Sito Web</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
                placeholder="www.negozio.it"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Orari Apertura</label>
              <input
                type="text"
                value={formData.opening_hours}
                onChange={(e) => setFormData({...formData, opening_hours: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
                placeholder="Lun-Sab 9:00-19:00"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Descrizione</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border h-20"
                placeholder="Descrivi il tuo negozio..."
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowAddShop(false)}
                className="flex-1 py-3 border border-border text-foreground hover:bg-muted"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Aggiungendo...' : 'Aggiungi Negozio'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Shop Detail Modal
  const ShopDetailModal = ({ shop, onClose }) => (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card border border-border p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <span className="text-2xl">{categories.find(c => c.id === shop.category)?.icon}</span>
          <button onClick={onClose} className="p-2 hover:bg-muted">
            <X className="w-5 h-5" />
          </button>
        </div>
        <h2 className="font-cinzel text-xl font-bold mb-2">{shop.name}</h2>
        <p className="text-sm text-primary mb-4">{categories.find(c => c.id === shop.category)?.name}</p>
        
        <div className="space-y-3 text-sm">
          {shop.description && <p className="text-muted-foreground">{shop.description}</p>}
          <div className="flex items-start gap-2">
            <span>📍</span>
            <span>{shop.address}, {shop.city}, {shop.region}</span>
          </div>
          {shop.phone && (
            <div className="flex items-center gap-2">
              <span>📞</span>
              <a href={`tel:${shop.phone}`} className="text-primary hover:underline">{shop.phone}</a>
            </div>
          )}
          {shop.email && (
            <div className="flex items-center gap-2">
              <span>✉️</span>
              <a href={`mailto:${shop.email}`} className="text-primary hover:underline">{shop.email}</a>
            </div>
          )}
          {shop.website && (
            <div className="flex items-center gap-2">
              <span>🌐</span>
              <a href={`https://${shop.website}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{shop.website}</a>
            </div>
          )}
          {shop.opening_hours && (
            <div className="flex items-center gap-2">
              <span>🕐</span>
              <span>{shop.opening_hours}</span>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full mt-6 py-2 border border-border hover:bg-muted"
        >
          Chiudi
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 flex items-center justify-center">
        <Horse className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="shops-page">
      <div className="h-[calc(100vh-5rem)] relative">
        {/* Filters Panel */}
        {showFilters ? (
          <div className="absolute top-4 left-14 z-[1000] bg-card border border-border p-4 max-w-xs" data-testid="shops-filters">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-cinzel font-bold">Negozi Equestri</h3>
              <button onClick={() => setShowFilters(false)} className="p-1 hover:bg-muted rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Country Filter */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setSelectedCountry(null)}
                className={`px-3 py-1.5 text-xs ${!selectedCountry ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
              >
                Tutti
              </button>
              <button
                onClick={() => setSelectedCountry('IT')}
                className={`px-3 py-1.5 text-xs ${selectedCountry === 'IT' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
              >
                🇮🇹 Italia
              </button>
              <button
                onClick={() => setSelectedCountry('CH')}
                className={`px-3 py-1.5 text-xs ${selectedCountry === 'CH' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
              >
                🇨🇭 Svizzera
              </button>
            </div>

            {/* Category Filter */}
            <div className="mb-3">
              <label className="text-xs text-muted-foreground block mb-2">Categoria</label>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`px-2 py-1 text-xs ${!selectedCategory ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                >
                  Tutte
                </button>
                {categories.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedCategory(c.id)}
                    className={`px-2 py-1 text-xs flex items-center gap-1 ${
                      selectedCategory === c.id ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    <span>{c.icon}</span>
                    <span>{c.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Add Shop Button */}
            {user ? (
              <button
                onClick={() => setShowAddShop(true)}
                className="w-full py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 flex items-center justify-center gap-2 mt-2"
              >
                <Plus className="w-5 h-5" />
                Aggiungi Negozio
              </button>
            ) : (
              <div className="mt-2 space-y-2">
                <Link to="/login" className="w-full py-2 bg-muted text-foreground text-sm text-center block hover:bg-muted/80 border border-border">
                  Accedi per aggiungere
                </Link>
                <Link to="/register" className="w-full py-2 bg-primary text-primary-foreground text-sm text-center block hover:opacity-90">
                  Registra il tuo negozio
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="absolute top-4 left-14 z-[1000] flex gap-2">
            <button onClick={() => setShowFilters(true)} className="bg-card border border-border p-3 hover:bg-muted" title="Mostra filtri">
              <span className="text-sm font-medium">⚙️ Filtri</span>
            </button>
            {user && (
              <button onClick={() => setShowAddShop(true)} className="bg-primary text-primary-foreground p-3 hover:opacity-90 flex items-center gap-2">
                <Plus className="w-5 h-5" />
                <span className="text-sm font-medium">Nuovo Negozio</span>
              </button>
            )}
          </div>
        )}

        {/* Map */}
        <MapContainer center={center} zoom={7} style={{ height: '100%', width: '100%' }} className="z-0">
          <TileLayer
            attribution='&copy; <a href="https://carto.com/">CartoDB</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          {shops.map(shop => (
            <Marker
              key={shop.id}
              position={[shop.lat, shop.lng]}
              icon={shop.country === 'IT' ? goldIcon : orangeIcon}
            >
              <Popup>
                <div className="p-2 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{categories.find(c => c.id === shop.category)?.icon}</span>
                    <span>{shop.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
                  </div>
                  <h4 className="font-bold text-gray-900">{shop.name}</h4>
                  <p className="text-sm text-blue-600">{categories.find(c => c.id === shop.category)?.name}</p>
                  <p className="text-sm text-gray-600">{shop.city}, {shop.region}</p>
                  {shop.phone && <p className="text-xs text-gray-500 mt-1">📞 {shop.phone}</p>}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Shops List Sidebar */}
        {showShopsList ? (
          <div className="absolute top-4 right-4 z-[1000] bg-card border border-border p-4 max-h-[80vh] overflow-y-auto w-80" data-testid="shops-list">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-cinzel font-bold">Negozi ({shops.length})</h3>
              <button onClick={() => setShowShopsList(false)} className="p-1 hover:bg-muted rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              {shops.map(shop => (
                <div 
                  key={shop.id}
                  onClick={() => setSelectedShop(shop)}
                  className="p-3 bg-muted border border-border hover:border-primary transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span>{categories.find(c => c.id === shop.category)?.icon}</span>
                    <span>{shop.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
                    {shop.verified && <span className="text-xs text-green-400">✓</span>}
                  </div>
                  <p className="font-medium text-sm">{shop.name}</p>
                  <p className="text-xs text-muted-foreground">{shop.city}, {shop.region}</p>
                  {shop.phone && <p className="text-xs text-primary mt-1">📞 {shop.phone}</p>}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowShopsList(true)}
            className="absolute top-4 right-4 z-[1000] bg-card border border-border p-3 hover:bg-muted"
          >
            <span className="text-sm font-medium">🏪 Negozi ({shops.length})</span>
          </button>
        )}

        {/* Modals */}
        {showAddShop && <AddShopForm />}
        {selectedShop && <ShopDetailModal shop={selectedShop} onClose={() => setSelectedShop(null)} />}
      </div>
    </div>
  );
};

// Auctions Page Component
const AuctionsPage = () => {
  const { t } = useTranslation();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState({});
  const [bidderName, setBidderName] = useState('');

  useEffect(() => {
    fetchAuctions();
  }, []);

  const fetchAuctions = async () => {
    try {
      const response = await axios.get(`${API}/auctions`);
      setAuctions(response.data);
    } catch (error) {
      console.error('Error fetching auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const placeBid = async (auctionId, currentBid, minIncrement) => {
    const amount = bidAmount[auctionId];
    if (!amount || amount <= currentBid) {
      alert(`L'offerta deve essere maggiore di €${currentBid.toLocaleString()}`);
      return;
    }
    if (!bidderName.trim()) {
      alert('Inserisci il tuo nome');
      return;
    }

    try {
      await axios.post(`${API}/auctions/${auctionId}/bid`, {
        auction_id: auctionId,
        bidder_name: bidderName,
        amount: parseFloat(amount)
      });
      fetchAuctions();
      setBidAmount({ ...bidAmount, [auctionId]: '' });
    } catch (error) {
      alert(error.response?.data?.detail || 'Errore nell\'offerta');
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20 px-6" data-testid="auctions-page">
      <div className="max-w-7xl mx-auto py-12">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 className="font-cinzel text-4xl font-bold">{t('auctions.title')}</h1>
            <p className="text-muted-foreground mt-2">Focus: Verona Fiere • Commissione 7%</p>
          </div>
          <div className="bg-card border border-border p-4">
            <label className="text-sm text-muted-foreground block mb-2">Il tuo nome</label>
            <input
              type="text"
              value={bidderName}
              onChange={(e) => setBidderName(e.target.value)}
              className="px-4 py-2 bg-muted border border-border text-foreground w-48"
              placeholder="Nome offerente"
              data-testid="bidder-name-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <Gavel className="w-12 h-12 text-primary animate-pulse mx-auto" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {auctions.map(auction => (
              <div 
                key={auction.id}
                className="bg-card border border-border overflow-hidden"
                data-testid={`auction-item-${auction.id}`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-3 py-1 text-sm ${auction.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-muted'}`}>
                      {auction.status === 'active' ? 'Attiva' : auction.status}
                    </span>
                    <span className="text-sm text-muted-foreground">{t('auctions.commission')}: 7%</span>
                  </div>

                  <h3 className="font-cinzel text-xl font-bold mb-2">{auction.horse_name}</h3>
                  
                  <div className="grid grid-cols-2 gap-4 my-6">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('auctions.startPrice')}</p>
                      <p className="font-mono font-bold">€{auction.start_price?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('auctions.currentBid')}</p>
                      <p className="font-mono font-bold text-primary text-xl">€{auction.current_bid?.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-muted-foreground">{t('auctions.endDate')}</p>
                    <p className="font-medium">{auction.end_date}</p>
                  </div>

                  {auction.bids?.length > 0 && (
                    <div className="mb-4 p-3 bg-muted">
                      <p className="text-xs text-muted-foreground mb-2">Ultime offerte:</p>
                      {auction.bids.slice(-3).reverse().map((bid, i) => (
                        <p key={i} className="text-sm">
                          {bid.bidder_name}: <span className="font-mono">€{bid.amount.toLocaleString()}</span>
                        </p>
                      ))}
                    </div>
                  )}

                  {auction.status === 'active' && (
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-muted-foreground block mb-1">
                          {t('auctions.minIncrement')}: €{auction.min_increment?.toLocaleString()}
                        </label>
                        <input
                          type="number"
                          value={bidAmount[auction.id] || ''}
                          onChange={(e) => setBidAmount({ ...bidAmount, [auction.id]: e.target.value })}
                          placeholder={`Min €${(auction.current_bid + auction.min_increment).toLocaleString()}`}
                          className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                          data-testid={`bid-input-${auction.id}`}
                        />
                      </div>
                      <button
                        onClick={() => placeBid(auction.id, auction.current_bid, auction.min_increment)}
                        className="w-full px-4 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
                        data-testid={`place-bid-btn-${auction.id}`}
                      >
                        {t('auctions.placeBid')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Certificates Page Component
const CertificatesPage = () => {
  const { t } = useTranslation();
  const [certificates, setCertificates] = useState([]);
  const [horses, setHorses] = useState([]);
  const [selectedHorse, setSelectedHorse] = useState('');
  const [certType, setCertType] = useState('health');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [verifyHash, setVerifyHash] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [selectedCert, setSelectedCert] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Advanced fields
  const [microchipNumber, setMicrochipNumber] = useState('');
  const [passportNumber, setPassportNumber] = useState('');
  const [fiseId, setFiseId] = useState('');
  const [feiId, setFeiId] = useState('');
  const [vetName, setVetName] = useState('');
  const [vetLicense, setVetLicense] = useState('');
  const [vetAsl, setVetAsl] = useState('');
  const [healthStatus, setHealthStatus] = useState('Idoneo');
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [vaccinations, setVaccinations] = useState([]);
  const [newVax, setNewVax] = useState({ name: '', date: '', batch: '' });

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-fill horse data when horse is selected
  useEffect(() => {
    if (selectedHorse) {
      const horse = horses.find(h => h.id === selectedHorse);
      if (horse) {
        setFiseId(horse.fise_id || '');
        setMicrochipNumber(horse.microchip_number || '');
        setPassportNumber(horse.passport_number || '');
        setFeiId(horse.fei_id || '');
      }
    }
  }, [selectedHorse, horses]);

  const fetchData = async () => {
    try {
      const [certsRes, horsesRes] = await Promise.all([
        axios.get(`${API}/certificates`),
        axios.get(`${API}/horses`)
      ]);
      setCertificates(certsRes.data);
      setHorses(horsesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addVaccination = () => {
    if (newVax.name && newVax.date) {
      setVaccinations([...vaccinations, { ...newVax }]);
      setNewVax({ name: '', date: '', batch: '' });
    }
  };

  const removeVaccination = (index) => {
    setVaccinations(vaccinations.filter((_, i) => i !== index));
  };

  const generateCertificate = async () => {
    if (!selectedHorse) {
      alert('Seleziona un cavallo');
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        horse_id: selectedHorse,
        certificate_type: certType,
        microchip_number: microchipNumber,
        passport_number: passportNumber,
        fise_id: fiseId,
        fei_id: feiId,
        veterinarian_name: vetName,
        veterinarian_license: vetLicense,
        veterinarian_asl: vetAsl,
        health_status: healthStatus,
        clinical_notes: clinicalNotes,
        vaccinations: vaccinations.length > 0 ? vaccinations : [],
      };
      await axios.post(`${API}/certificates`, payload);
      fetchData();
      // Reset form
      setMicrochipNumber(''); setPassportNumber(''); setFiseId(''); setFeiId('');
      setVetName(''); setVetLicense(''); setVetAsl('');
      setHealthStatus('Idoneo'); setClinicalNotes(''); setVaccinations([]);
      setSelectedHorse('');
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert('Errore nella generazione del certificato');
    } finally {
      setGenerating(false);
    }
  };

  const verifyCertificate = async () => {
    if (!verifyHash.trim()) return;
    try {
      const response = await axios.get(`${API}/certificates/verify/${verifyHash}`);
      setVerifyResult(response.data);
    } catch (error) {
      setVerifyResult({ valid: false, error: 'Certificato non trovato' });
    }
  };

  const downloadPDF = async (certId) => {
    try {
      const response = await axios.get(`${API}/certificates/${certId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificato_${certId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Errore nel download del PDF');
    }
  };

  const certTypeLabels = {
    'health': 'Sanitario UE',
    'export': 'Export IT/CH',
    'competition': 'Competizione FISE/FEI',
    'passport': 'Passaporto Equino UE'
  };

  // Certificate Detail Modal
  const CertificateDetailModal = ({ cert, onClose }) => (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card border border-border p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-cinzel text-xl font-bold">Dettagli Certificato</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted" data-testid="close-cert-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Status + Certificate Number */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <span className={`px-4 py-2 text-sm font-bold ${cert.status === 'valid' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {cert.status === 'valid' ? 'VALIDO' : 'SCADUTO'}
            </span>
            <span className="text-muted-foreground">{certTypeLabels[cert.certificate_type] || cert.certificate_type}</span>
          </div>
          {cert.certificate_number && (
            <span className="font-mono text-sm text-primary bg-primary/10 px-3 py-1">{cert.certificate_number}</span>
          )}
        </div>

        {/* Horse Info */}
        <div className="bg-muted p-4 mb-4">
          <h3 className="font-bold mb-3 text-primary text-sm uppercase tracking-wider">Identificazione Equide</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Nome:</span> <span className="font-medium">{cert.horse_name}</span></div>
            <div><span className="text-muted-foreground">Microchip:</span> <span className="font-mono text-xs">{cert.microchip_number || 'N/A'}</span></div>
            <div><span className="text-muted-foreground">FISE ID:</span> <span className="font-mono text-xs">{cert.fise_id || 'N/A'}</span></div>
            <div><span className="text-muted-foreground">FEI ID:</span> <span className="font-mono text-xs">{cert.fei_id || 'N/A'}</span></div>
            <div><span className="text-muted-foreground">Passaporto:</span> <span className="font-mono text-xs">{cert.passport_number || 'N/A'}</span></div>
            <div><span className="text-muted-foreground">Stato Sanitario:</span> <span className={`font-bold ${cert.health_status === 'Idoneo' ? 'text-green-400' : 'text-red-400'}`}>{cert.health_status || 'N/A'}</span></div>
            <div><span className="text-muted-foreground">Emissione:</span> {cert.issue_date}</div>
            <div><span className="text-muted-foreground">Scadenza:</span> {cert.expiry_date}</div>
            <div className="col-span-2"><span className="text-muted-foreground">Autorita:</span> {cert.issuing_authority}</div>
          </div>
        </div>

        {/* Veterinarian */}
        {cert.veterinarian_name && (
          <div className="bg-muted p-4 mb-4">
            <h3 className="font-bold mb-3 text-primary text-sm uppercase tracking-wider">Veterinario Ufficiale</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">Nome:</span> {cert.veterinarian_name}</div>
              <div><span className="text-muted-foreground">N. Iscrizione:</span> {cert.veterinarian_license || 'N/A'}</div>
              <div><span className="text-muted-foreground">ASL:</span> {cert.veterinarian_asl || 'N/A'}</div>
            </div>
          </div>
        )}

        {/* Vaccinations */}
        {cert.vaccinations && cert.vaccinations.length > 0 && (
          <div className="bg-muted p-4 mb-4">
            <h3 className="font-bold mb-3 text-primary text-sm uppercase tracking-wider">Vaccinazioni</h3>
            <div className="space-y-2">
              {cert.vaccinations.map((v, i) => (
                <div key={i} className="flex items-center gap-4 text-sm bg-background/50 p-2">
                  <span className="font-medium">{v.name}</span>
                  <span className="text-muted-foreground">{v.date}</span>
                  <span className="text-xs text-muted-foreground font-mono">Lotto: {v.batch || 'N/A'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Blockchain Info */}
        <div className="bg-muted p-4 mb-4">
          <h3 className="font-bold mb-3 text-primary text-sm uppercase tracking-wider">Verifica Blockchain (Bitcoin)</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Hash SHA-256:</span>
              <p className="font-mono text-xs break-all mt-1">{cert.blockchain_hash}</p>
            </div>
            {cert.blockchain_timestamp && (
              <div><span className="text-muted-foreground">Timestamp:</span> {cert.blockchain_timestamp}</div>
            )}
            <div>
              <span className="text-muted-foreground">Network:</span> {cert.blockchain_network || 'OpenTimestamps (Bitcoin)'}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Stato:</span>
              <span className={`px-2 py-0.5 text-xs ${
                cert.ots_status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                cert.ots_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-gray-500/20 text-gray-400'
              }`}>
                {cert.ots_status === 'confirmed' ? 'Confermato su Bitcoin' :
                 cert.ots_status === 'pending' ? 'In attesa conferma (2-6 ore)' :
                 'Locale'}
              </span>
            </div>
            {cert.ots_verify_url && (
              <a 
                href={cert.ots_verify_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 mt-2"
              >
                <Shield className="w-4 h-4" />
                Verifica su OpenTimestamps.org
              </a>
            )}
          </div>
        </div>

        {/* Official Links */}
        <div className="bg-muted p-4 mb-4">
          <h3 className="font-bold mb-3 text-primary text-sm uppercase tracking-wider">Verifica Enti Ufficiali</h3>
          <div className="space-y-2 text-sm">
            {cert.fise_verification_url && (
              <a href={cert.fise_verification_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-400 hover:text-blue-300">
                <Globe className="w-4 h-4" /> FISE - Verifica Tesseramento
              </a>
            )}
            {cert.fei_verification_url && (
              <a href={cert.fei_verification_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-400 hover:text-blue-300">
                <Globe className="w-4 h-4" /> FEI - Performance Horse
              </a>
            )}
            <a href="https://webgate.ec.europa.eu/tracesnt" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-400 hover:text-blue-300">
              <Globe className="w-4 h-4" /> EU TRACES - Certificati Sanitari UE
            </a>
            <a href="https://www.salute.gov.it/portale/temi/p2_4.jsp?lingua=italiano&area=sanitaAnimale" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-400 hover:text-blue-300">
              <Globe className="w-4 h-4" /> Ministero della Salute
            </a>
          </div>
        </div>

        {/* QR Code */}
        <div className="flex justify-center p-6 bg-white mb-4">
          <QRCodeSVG value={cert.qr_data} size={150} level="M" />
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => downloadPDF(cert.id)}
            className="flex-1 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 flex items-center justify-center gap-2"
            data-testid="download-pdf-btn"
          >
            <Upload className="w-5 h-5 rotate-180" />
            Scarica PDF
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-border text-foreground hover:bg-muted"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-20 px-6" data-testid="certificates-page">
      <div className="max-w-7xl mx-auto py-12">
        <h1 className="font-cinzel text-4xl font-bold mb-2">{t('certificates.title')}</h1>
        <p className="text-muted-foreground mb-12">Certificati conformi EU con verifica blockchain Bitcoin (OpenTimestamps) e riferimenti FISE/FEI</p>

        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Generate Certificate - Enhanced Form */}
          <div className="lg:col-span-2 bg-card border border-border p-6" data-testid="generate-cert-section">
            <h2 className="font-cinzel text-xl font-bold mb-6">{t('certificates.generate')}</h2>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground block mb-2">Cavallo *</label>
                  <select
                    value={selectedHorse}
                    onChange={(e) => setSelectedHorse(e.target.value)}
                    className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                    data-testid="horse-select"
                  >
                    <option value="">Seleziona cavallo</option>
                    {horses.map(h => (
                      <option key={h.id} value={h.id}>{h.name} ({h.region})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground block mb-2">Tipo Certificato *</label>
                  <select
                    value={certType}
                    onChange={(e) => setCertType(e.target.value)}
                    className="w-full px-4 py-2 bg-muted border border-border text-foreground"
                    data-testid="cert-type-select"
                  >
                    <option value="health">Sanitario UE (Reg. 2021/403)</option>
                    <option value="export">Export IT/CH (TRACES)</option>
                    <option value="competition">Competizione FISE/FEI</option>
                    <option value="passport">Passaporto Equino (Reg. 2015/262)</option>
                  </select>
                </div>
              </div>

              {/* Toggle advanced fields */}
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-sm text-primary hover:underline flex items-center gap-1"
                data-testid="toggle-advanced-btn"
              >
                <ChevronRight className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
                {showAdvanced ? 'Nascondi campi avanzati' : 'Mostra campi avanzati (FISE, FEI, Veterinario, Vaccinazioni)'}
              </button>

              {showAdvanced && (
                <div className="space-y-4 border-t border-border pt-4">
                  {/* Identification */}
                  <h3 className="text-sm font-bold text-primary uppercase tracking-wider">Identificazione Equide</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">N. Microchip</label>
                      <input type="text" value={microchipNumber} onChange={(e) => setMicrochipNumber(e.target.value)}
                        placeholder="es. 380098100012345" className="w-full px-3 py-2 bg-muted border border-border text-foreground text-sm font-mono" data-testid="microchip-input" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">N. Passaporto Equino</label>
                      <input type="text" value={passportNumber} onChange={(e) => setPassportNumber(e.target.value)}
                        placeholder="es. IT-076-2020-00123" className="w-full px-3 py-2 bg-muted border border-border text-foreground text-sm font-mono" data-testid="passport-input" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">FISE ID</label>
                      <input type="text" value={fiseId} onChange={(e) => setFiseId(e.target.value)}
                        placeholder="es. IT-FIS-2020-1234" className="w-full px-3 py-2 bg-muted border border-border text-foreground text-sm font-mono" data-testid="fise-id-input" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">FEI ID</label>
                      <input type="text" value={feiId} onChange={(e) => setFeiId(e.target.value)}
                        placeholder="es. FEI-106XX99" className="w-full px-3 py-2 bg-muted border border-border text-foreground text-sm font-mono" data-testid="fei-id-input" />
                    </div>
                  </div>

                  {/* Veterinarian */}
                  <h3 className="text-sm font-bold text-primary uppercase tracking-wider mt-4">Veterinario Ufficiale</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Nome Veterinario</label>
                      <input type="text" value={vetName} onChange={(e) => setVetName(e.target.value)}
                        placeholder="Dr. Mario Rossi" className="w-full px-3 py-2 bg-muted border border-border text-foreground text-sm" data-testid="vet-name-input" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">N. Iscrizione Albo</label>
                      <input type="text" value={vetLicense} onChange={(e) => setVetLicense(e.target.value)}
                        placeholder="es. FI-1234" className="w-full px-3 py-2 bg-muted border border-border text-foreground text-sm font-mono" data-testid="vet-license-input" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">ASL Competente</label>
                      <input type="text" value={vetAsl} onChange={(e) => setVetAsl(e.target.value)}
                        placeholder="es. ASL Toscana Centro" className="w-full px-3 py-2 bg-muted border border-border text-foreground text-sm" data-testid="vet-asl-input" />
                    </div>
                  </div>

                  {/* Health Status */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Stato Sanitario</label>
                      <select value={healthStatus} onChange={(e) => setHealthStatus(e.target.value)}
                        className="w-full px-3 py-2 bg-muted border border-border text-foreground text-sm" data-testid="health-status-select">
                        <option value="Idoneo">Idoneo</option>
                        <option value="Idoneo con riserva">Idoneo con riserva</option>
                        <option value="Non idoneo">Non idoneo</option>
                        <option value="In osservazione">In osservazione</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Note Cliniche</label>
                      <input type="text" value={clinicalNotes} onChange={(e) => setClinicalNotes(e.target.value)}
                        placeholder="Eventuali note cliniche" className="w-full px-3 py-2 bg-muted border border-border text-foreground text-sm" data-testid="clinical-notes-input" />
                    </div>
                  </div>

                  {/* Vaccinations */}
                  <h3 className="text-sm font-bold text-primary uppercase tracking-wider mt-4">Vaccinazioni</h3>
                  {vaccinations.length > 0 && (
                    <div className="space-y-2">
                      {vaccinations.map((v, i) => (
                        <div key={i} className="flex items-center gap-3 bg-muted p-2 text-sm">
                          <span className="font-medium">{v.name}</span>
                          <span className="text-muted-foreground">{v.date}</span>
                          <span className="text-xs text-muted-foreground">Lotto: {v.batch || '-'}</span>
                          <button onClick={() => removeVaccination(i)} className="ml-auto text-red-400 hover:text-red-300" data-testid={`remove-vax-${i}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-2">
                    <input type="text" value={newVax.name} onChange={(e) => setNewVax({ ...newVax, name: e.target.value })}
                      placeholder="Vaccino (es. Influenza)" className="px-3 py-2 bg-muted border border-border text-foreground text-sm" data-testid="vax-name-input" />
                    <input type="date" value={newVax.date} onChange={(e) => setNewVax({ ...newVax, date: e.target.value })}
                      className="px-3 py-2 bg-muted border border-border text-foreground text-sm" data-testid="vax-date-input" />
                    <input type="text" value={newVax.batch} onChange={(e) => setNewVax({ ...newVax, batch: e.target.value })}
                      placeholder="N. Lotto" className="px-3 py-2 bg-muted border border-border text-foreground text-sm" data-testid="vax-batch-input" />
                    <button onClick={addVaccination} className="px-3 py-2 bg-accent text-white text-sm hover:opacity-90" data-testid="add-vax-btn">
                      + Aggiungi
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={generateCertificate}
                disabled={!selectedHorse || generating}
                className="w-full px-4 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="generate-cert-btn"
              >
                {generating ? (
                  <><Shield className="w-5 h-5 animate-spin" /> Generazione in corso...</>
                ) : (
                  <><Shield className="w-5 h-5" /> Genera Certificato con Blockchain</>
                )}
              </button>
              <p className="text-xs text-muted-foreground text-center">
                L'hash SHA-256 verra ancorato alla blockchain Bitcoin tramite OpenTimestamps. Conferma in 2-6 ore.
              </p>
            </div>
          </div>

          {/* Right column: Verify + Links */}
          <div className="space-y-6">
            {/* Verify Certificate */}
            <div className="bg-card border border-border p-6" data-testid="verify-cert-section">
              <h2 className="font-cinzel text-xl font-bold mb-6">{t('certificates.blockchain')}</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground block mb-2">Hash o N. Certificato</label>
                  <input
                    type="text"
                    value={verifyHash}
                    onChange={(e) => setVerifyHash(e.target.value)}
                    placeholder="Inserisci hash o primi 12 caratteri"
                    className="w-full px-4 py-2 bg-muted border border-border text-foreground font-mono text-sm"
                    data-testid="verify-hash-input"
                  />
                </div>
                <button
                  onClick={verifyCertificate}
                  className="w-full px-4 py-3 bg-accent text-white font-medium hover:opacity-90 transition-opacity"
                  data-testid="verify-cert-btn"
                >
                  Verifica Autenticita
                </button>
                {verifyResult && (
                  <div className={`p-4 ${verifyResult.valid ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'} border`}>
                    {verifyResult.valid ? (
                      <div>
                        <div className="flex items-center gap-2 text-green-400 mb-2">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-bold">Certificato Valido</span>
                        </div>
                        {verifyResult.certificate && (
                          <p className="text-xs text-muted-foreground">
                            N. {verifyResult.certificate.certificate_number} - {verifyResult.certificate.horse_name}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-400">
                        <AlertTriangle className="w-5 h-5" />
                        <span>{verifyResult.error || 'Non Valido'}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Official Links Card */}
            <div className="bg-card border border-border p-6" data-testid="official-links-section">
              <h2 className="font-cinzel text-lg font-bold mb-4">Enti Ufficiali</h2>
              <div className="space-y-3">
                <a href="https://www.fise.it" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-muted hover:bg-muted/80 transition-colors">
                  <span className="text-xl">🏇</span>
                  <div>
                    <p className="font-medium text-sm">FISE</p>
                    <p className="text-xs text-muted-foreground">Fed. Italiana Sport Equestri</p>
                  </div>
                </a>
                <a href="https://www.fei.org" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-muted hover:bg-muted/80 transition-colors">
                  <span className="text-xl">🌍</span>
                  <div>
                    <p className="font-medium text-sm">FEI</p>
                    <p className="text-xs text-muted-foreground">Federation Equestre Internationale</p>
                  </div>
                </a>
                <a href="https://webgate.ec.europa.eu/tracesnt" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-muted hover:bg-muted/80 transition-colors">
                  <span className="text-xl">🇪🇺</span>
                  <div>
                    <p className="font-medium text-sm">EU TRACES</p>
                    <p className="text-xs text-muted-foreground">Certificati Sanitari UE</p>
                  </div>
                </a>
                <a href="https://opentimestamps.org" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-muted hover:bg-muted/80 transition-colors">
                  <span className="text-xl font-mono text-orange-400">&#8383;</span>
                  <div>
                    <p className="font-medium text-sm">OpenTimestamps</p>
                    <p className="text-xs text-muted-foreground">Verifica Blockchain Bitcoin</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Certificates List */}
        <h2 className="font-cinzel text-2xl font-bold mb-6">Certificati Emessi</h2>
        {loading ? (
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-primary animate-pulse mx-auto" />
          </div>
        ) : certificates.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border">
            <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">Nessun certificato emesso</p>
            <p className="text-sm text-muted-foreground mt-2">Aggiungi un cavallo e genera il primo certificato blockchain</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certificates.map(cert => (
              <div 
                key={cert.id}
                className="bg-card border border-border p-6 hover:border-primary transition-colors cursor-pointer"
                data-testid={`cert-item-${cert.id}`}
                onClick={() => setSelectedCert(cert)}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-3 py-1 text-xs font-bold ${cert.status === 'valid' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {cert.status === 'valid' ? 'VALIDO' : 'SCADUTO'}
                  </span>
                  {cert.certificate_number && (
                    <span className="font-mono text-xs text-primary">{cert.certificate_number}</span>
                  )}
                </div>
                <h3 className="font-cinzel font-bold mb-1">{cert.horse_name}</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {certTypeLabels[cert.certificate_type] || cert.certificate_type}
                </p>
                
                <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Emissione</p>
                    <p className="text-sm">{cert.issue_date}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Scadenza</p>
                    <p className="text-sm">{cert.expiry_date}</p>
                  </div>
                </div>

                {/* Blockchain badge */}
                <div className="text-xs mb-3 flex items-center gap-2">
                  <span className={`px-2 py-0.5 ${
                    cert.ots_status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                    cert.ots_status === 'pending' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {cert.ots_status === 'confirmed' ? 'Bitcoin Confirmed' :
                     cert.ots_status === 'pending' ? 'Bitcoin Pending' :
                     'Local'}
                  </span>
                </div>

                <p className="text-xs text-muted-foreground font-mono break-all">
                  {cert.blockchain_hash?.substring(0, 24)}...
                </p>

                {/* Action buttons */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={(e) => { e.stopPropagation(); downloadPDF(cert.id); }}
                    className="flex-1 py-2 bg-primary text-primary-foreground text-sm hover:opacity-90 flex items-center justify-center gap-1"
                    data-testid={`download-pdf-${cert.id}`}
                  >
                    <Upload className="w-4 h-4 rotate-180" /> PDF
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedCert(cert); }}
                    className="flex-1 py-2 bg-muted text-foreground text-sm hover:bg-muted/80 flex items-center justify-center gap-1"
                    data-testid={`view-cert-${cert.id}`}
                  >
                    <FileCheck className="w-4 h-4" /> Dettagli
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Certificate Detail Modal */}
        {selectedCert && <CertificateDetailModal cert={selectedCert} onClose={() => setSelectedCert(null)} />}
      </div>
    </div>
  );
};

// VAT Calculator Component
const VATCalculator = () => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [fromCountry, setFromCountry] = useState('IT');
  const [toCountry, setToCountry] = useState('CH');
  const [result, setResult] = useState(null);

  const calculate = async () => {
    if (!amount) return;
    try {
      const response = await axios.post(`${API}/vat/calculate`, {
        amount: parseFloat(amount),
        from_country: fromCountry,
        to_country: toCountry
      });
      setResult(response.data);
    } catch (error) {
      console.error('Error calculating VAT:', error);
    }
  };

  return (
    <div className="bg-card border border-border p-6" data-testid="vat-calculator">
      <h2 className="font-cinzel text-xl font-bold mb-6">{t('vat.title')} IT/CH</h2>
      <div className="space-y-4">
        <div>
          <label className="text-sm text-muted-foreground block mb-2">{t('vat.amount')} (€)</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full px-4 py-2 bg-muted border border-border text-foreground"
            placeholder="es. 50000"
            data-testid="vat-amount-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-muted-foreground block mb-2">{t('vat.from')}</label>
            <select
              value={fromCountry}
              onChange={(e) => setFromCountry(e.target.value)}
              className="w-full px-4 py-2 bg-muted border border-border text-foreground"
              data-testid="vat-from-select"
            >
              <option value="IT">🇮🇹 Italia</option>
              <option value="CH">🇨🇭 Svizzera</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-muted-foreground block mb-2">{t('vat.to')}</label>
            <select
              value={toCountry}
              onChange={(e) => setToCountry(e.target.value)}
              className="w-full px-4 py-2 bg-muted border border-border text-foreground"
              data-testid="vat-to-select"
            >
              <option value="CH">🇨🇭 Svizzera</option>
              <option value="IT">🇮🇹 Italia</option>
            </select>
          </div>
        </div>
        <button
          onClick={calculate}
          className="w-full px-4 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          data-testid="vat-calculate-btn"
        >
          <Calculator className="w-4 h-4" />
          {t('vat.calculate')}
        </button>

        {result && (
          <div className="p-4 bg-muted border border-border space-y-2" data-testid="vat-result">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Importo:</span>
              <span className="font-mono">€{result.gross_amount?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">IVA ({result.vat_rate}%):</span>
              <span className="font-mono">€{result.vat_amount?.toLocaleString()}</span>
            </div>
            {result.import_duty > 0 && (
              <div className="flex justify-between text-accent">
                <span>{t('vat.importDuty')}:</span>
                <span className="font-mono">€{result.import_duty?.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-primary border-t border-border pt-2">
              <span>{t('vat.total')}:</span>
              <span className="font-mono">€{result.total?.toLocaleString()}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">{result.note}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Sponsors Page Component
const SponsorsPage = () => {
  const { t } = useTranslation();
  const { user, authAxios } = useAuth();
  const [searchParams] = useSearchParams();
  const [sponsors, setSponsors] = useState([]);
  const [horses, setHorses] = useState([]);
  const [myHorses, setMyHorses] = useState([]);
  const [selectedHorse, setSelectedHorse] = useState('');
  const [matchedSponsors, setMatchedSponsors] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('my') ? 'find' : 'browse');
  const [sendingRequest, setSendingRequest] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [sponsorsRes, horsesRes] = await Promise.all([
        axios.get(`${API}/sponsors`),
        axios.get(`${API}/horses`)
      ]);
      setSponsors(sponsorsRes.data);
      setHorses(horsesRes.data);

      // If logged in, fetch user's horses and sent requests
      if (user && authAxios) {
        try {
          const [myHorsesRes, requestsRes] = await Promise.all([
            authAxios.get('/my/horses'),
            authAxios.get('/my/sponsorship-requests/sent')
          ]);
          setMyHorses(myHorsesRes.data);
          setSentRequests(requestsRes.data);
        } catch (e) {
          console.log('Not logged in or error fetching user data');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const matchSponsors = async () => {
    if (!selectedHorse) {
      alert('Seleziona un cavallo');
      return;
    }
    try {
      const response = await axios.get(`${API}/sponsors/match/${selectedHorse}`);
      setMatchedSponsors(response.data);
    } catch (error) {
      console.error('Error matching sponsors:', error);
    }
  };

  const sendSponsorshipRequest = async (sponsorId) => {
    if (!user) {
      alert('Devi effettuare il login per inviare richieste');
      return;
    }
    if (!selectedHorse) {
      alert('Seleziona un cavallo');
      return;
    }

    setSendingRequest(sponsorId);
    try {
      await authAxios.post('/sponsorship-requests', {
        horse_id: selectedHorse,
        sponsor_id: sponsorId,
        message: requestMessage
      });
      alert('Richiesta inviata con successo!');
      fetchData();
      setRequestMessage('');
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingRequest(null);
    }
  };

  const isRequestSent = (sponsorId) => {
    return sentRequests.some(r => r.sponsor_id === sponsorId && r.horse_id === selectedHorse);
  };

  const getRequestStatus = (sponsorId) => {
    const req = sentRequests.find(r => r.sponsor_id === sponsorId && r.horse_id === selectedHorse);
    return req?.status;
  };

  const typeColors = {
    feed: 'bg-green-500/20 text-green-400',
    equipment: 'bg-blue-500/20 text-blue-400',
    transport: 'bg-purple-500/20 text-purple-400',
    training: 'bg-orange-500/20 text-orange-400',
    veterinary: 'bg-red-500/20 text-red-400',
    insurance: 'bg-cyan-500/20 text-cyan-400'
  };

  const typeIcons = {
    feed: '🌾', equipment: '🎯', transport: '🚚', training: '🏆', veterinary: '⚕️', insurance: '🛡️'
  };

  return (
    <div className="min-h-screen bg-background pt-20 px-6" data-testid="sponsors-page">
      <div className="max-w-7xl mx-auto py-12">
        <h1 className="font-cinzel text-4xl font-bold mb-4">{t('sponsors.title')}</h1>
        <p className="text-muted-foreground mb-8">Matching sponsor IT/CH con alert dazi import</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-border pb-4">
          <button
            onClick={() => setActiveTab('browse')}
            className={`px-6 py-3 font-medium transition-colors ${activeTab === 'browse' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
            data-testid="tab-browse"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Tutti gli Sponsor
          </button>
          <button
            onClick={() => setActiveTab('find')}
            className={`px-6 py-3 font-medium transition-colors ${activeTab === 'find' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
            data-testid="tab-find"
          >
            <Star className="w-4 h-4 inline mr-2" />
            Cerca Sponsor per i Tuoi Cavalli
          </button>
          {user && sentRequests.length > 0 && (
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-6 py-3 font-medium transition-colors ${activeTab === 'requests' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
              data-testid="tab-requests"
            >
              <FileCheck className="w-4 h-4 inline mr-2" />
              Le Mie Richieste ({sentRequests.length})
            </button>
          )}
        </div>

        {/* Tab: Find Sponsor for Your Horses */}
        {activeTab === 'find' && (
          <div className="space-y-8">
            {!user ? (
              <div className="bg-card border border-border p-8 text-center">
                <LogIn className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-cinzel text-xl font-bold mb-2">Accedi per Cercare Sponsor</h3>
                <p className="text-muted-foreground mb-6">Devi effettuare il login per cercare sponsor per i tuoi cavalli</p>
                <Link to="/login" className="px-6 py-3 bg-primary text-primary-foreground inline-block">Accedi</Link>
              </div>
            ) : myHorses.length === 0 ? (
              <div className="bg-card border border-border p-8 text-center">
                <Horse className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-cinzel text-xl font-bold mb-2">Nessun Cavallo</h3>
                <p className="text-muted-foreground mb-6">Aggiungi prima un cavallo per cercare sponsor</p>
                <Link to="/dashboard/horses" className="px-6 py-3 bg-primary text-primary-foreground inline-block">Aggiungi Cavallo</Link>
              </div>
            ) : (
              <>
                {/* Select Horse */}
                <div className="bg-card border border-border p-6">
                  <h2 className="font-cinzel text-xl font-bold mb-4">1. Seleziona il tuo cavallo</h2>
                  <select
                    value={selectedHorse}
                    onChange={(e) => { setSelectedHorse(e.target.value); setMatchedSponsors([]); }}
                    className="w-full px-4 py-3 bg-muted border border-border text-foreground"
                    data-testid="my-horse-select"
                  >
                    <option value="">Seleziona un cavallo</option>
                    {myHorses.map(h => (
                      <option key={h.id} value={h.id}>
                        {h.country === 'IT' ? '🇮🇹' : '🇨🇭'} {h.name} ({h.breed}, {h.age} anni) - €{h.price_eur?.toLocaleString()}
                      </option>
                    ))}
                  </select>
                  {selectedHorse && (
                    <button
                      onClick={matchSponsors}
                      className="mt-4 px-8 py-3 bg-accent text-white font-medium hover:opacity-90"
                      data-testid="find-sponsors-btn"
                    >
                      <Star className="w-4 h-4 inline mr-2" />
                      Trova Sponsor Compatibili
                    </button>
                  )}
                </div>

                {/* Matched Sponsors */}
                {matchedSponsors.length > 0 && (
                  <div className="bg-card border border-border p-6">
                    <h2 className="font-cinzel text-xl font-bold mb-4">2. Sponsor Compatibili ({matchedSponsors.length})</h2>
                    <p className="text-muted-foreground mb-4">Invia una richiesta di sponsorizzazione agli sponsor interessati</p>
                    
                    <div className="mb-4">
                      <label className="block text-sm text-muted-foreground mb-2">Messaggio per gli sponsor (opzionale)</label>
                      <textarea
                        value={requestMessage}
                        onChange={(e) => setRequestMessage(e.target.value)}
                        className="w-full px-4 py-3 bg-muted border border-border h-20 resize-none"
                        placeholder="Descrivi perché cerchi sponsorizzazione, le tue necessità..."
                        data-testid="sponsor-message"
                      />
                    </div>

                    <div className="space-y-4">
                      {matchedSponsors.map(sponsor => (
                        <div 
                          key={sponsor.id}
                          className={`p-4 border ${sponsor.cross_border ? 'border-accent bg-accent/10' : 'border-border'}`}
                          data-testid={`sponsor-match-${sponsor.id}`}
                        >
                          <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center gap-3">
                              <span className="text-3xl">{sponsor.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
                              <span className="text-2xl">{typeIcons[sponsor.type] || '🏢'}</span>
                              <div>
                                <p className="font-bold">{sponsor.name}</p>
                                <p className="text-sm text-muted-foreground">{sponsor.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <span className={`px-2 py-1 text-xs ${typeColors[sponsor.type] || 'bg-muted'}`}>
                                  {sponsor.type}
                                </span>
                                {sponsor.cross_border && (
                                  <span className="ml-2 px-2 py-1 text-xs bg-accent text-white">Cross-Border</span>
                                )}
                                <p className="text-sm text-primary font-mono mt-1">Compatibilità: {sponsor.match_score}%</p>
                              </div>
                              {isRequestSent(sponsor.id) ? (
                                <div className={`px-4 py-2 text-sm ${
                                  getRequestStatus(sponsor.id) === 'accepted' ? 'bg-green-500/20 text-green-400' :
                                  getRequestStatus(sponsor.id) === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                  'bg-muted text-muted-foreground'
                                }`}>
                                  {getRequestStatus(sponsor.id) === 'accepted' ? '✓ Accettata' :
                                   getRequestStatus(sponsor.id) === 'rejected' ? '✗ Rifiutata' : '⏳ In Attesa'}
                                </div>
                              ) : (
                                <button
                                  onClick={() => sendSponsorshipRequest(sponsor.id)}
                                  disabled={sendingRequest === sponsor.id}
                                  className="px-4 py-2 bg-primary text-primary-foreground text-sm hover:opacity-90 disabled:opacity-50"
                                  data-testid={`send-request-${sponsor.id}`}
                                >
                                  {sendingRequest === sponsor.id ? 'Invio...' : 'Invia Richiesta'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Tab: My Requests */}
        {activeTab === 'requests' && user && (
          <div className="space-y-4">
            <h2 className="font-cinzel text-xl font-bold mb-4">Le Mie Richieste di Sponsorizzazione</h2>
            {sentRequests.length === 0 ? (
              <div className="bg-card border border-border p-8 text-center">
                <FileCheck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Non hai ancora inviato richieste</p>
              </div>
            ) : (
              sentRequests.map(req => (
                <div key={req.id} className="bg-card border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold">{req.horse_name} → {req.sponsor_name}</p>
                      {req.message && <p className="text-sm text-muted-foreground mt-1">"{req.message}"</p>}
                    </div>
                    <span className={`px-3 py-1 text-sm ${
                      req.status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                      req.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {req.status === 'accepted' ? '✓ Accettata' :
                       req.status === 'rejected' ? '✗ Rifiutata' : '⏳ In Attesa'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Tab: Browse All Sponsors */}
        {activeTab === 'browse' && (
          <>
            {/* Match Sponsors (public) */}
            <div className="bg-card border border-border p-6 mb-12" data-testid="match-sponsors-section">
              <h2 className="font-cinzel text-xl font-bold mb-6">{t('sponsors.match')}</h2>
              <div className="flex flex-col md:flex-row gap-4">
                <select
                  value={selectedHorse}
                  onChange={(e) => setSelectedHorse(e.target.value)}
                  className="flex-1 px-4 py-3 bg-muted border border-border text-foreground"
                  data-testid="sponsor-horse-select"
                >
                  <option value="">Seleziona cavallo per abbinamento</option>
                  {horses.map(h => (
                    <option key={h.id} value={h.id}>
                      {h.country === 'IT' ? '🇮🇹' : '🇨🇭'} {h.name} ({h.region})
                    </option>
                  ))}
                </select>
                <button
                  onClick={matchSponsors}
                  className="px-8 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
                  data-testid="match-sponsors-btn"
                >
                  {t('sponsors.match')}
                </button>
              </div>

              {matchedSponsors.length > 0 && (
                <div className="mt-8 space-y-4" data-testid="matched-sponsors-list">
                  <h3 className="font-bold">Sponsor Abbinati ({matchedSponsors.length})</h3>
                  {matchedSponsors.map((sponsor, i) => (
                    <div 
                      key={sponsor.id}
                      className={`p-4 border ${sponsor.cross_border ? 'border-accent bg-accent/10' : 'border-border'}`}
                      data-testid={`matched-sponsor-${sponsor.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{sponsor.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
                          <div>
                            <p className="font-bold">{sponsor.name}</p>
                            <p className="text-sm text-muted-foreground">{sponsor.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs ${typeColors[sponsor.type]}`}>
                              {sponsor.type}
                            </span>
                            {sponsor.cross_border && (
                              <span className="px-2 py-1 text-xs bg-accent text-white">
                                {t('sponsors.crossBorder')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-primary font-mono mt-2">
                            {t('sponsors.matchScore')}: {sponsor.match_score}%
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* All Sponsors */}
            <h2 className="font-cinzel text-2xl font-bold mb-6">Tutti gli Sponsor</h2>
            {loading ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-primary animate-pulse mx-auto" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sponsors.map(sponsor => (
                  <div 
                    key={sponsor.id}
                    className="bg-card border border-border p-6 hover:border-primary transition-colors"
                    data-testid={`sponsor-item-${sponsor.id}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-3xl">{sponsor.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
                      <span className="text-2xl">{typeIcons[sponsor.type] || '🏢'}</span>
                      <span className={`px-3 py-1 text-sm ${typeColors[sponsor.type] || 'bg-muted'}`}>
                        {sponsor.type}
                      </span>
                    </div>
                    <h3 className="font-cinzel font-bold text-lg">{sponsor.name}</h3>
                    <p className="text-sm text-muted-foreground mt-2">{sponsor.description}</p>
                    {sponsor.website && (
                      <a href={sponsor.website} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline mt-2 block">
                        {sponsor.website}
                      </a>
                    )}
                    <a 
                      href={`mailto:${sponsor.contact_email}`}
                      className="text-muted-foreground text-sm hover:text-primary mt-2 block"
                    >
                      {sponsor.contact_email}
                    </a>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Subscription Page Component
const SubscriptionPage = () => {
  const { t } = useTranslation();
  const { user, authAxios } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    setLoading(true);
    try {
      const response = await authAxios.post('/subscription/checkout', {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.redirect_url) {
        // Piano gratuito
        navigate(response.data.redirect_url.replace(window.location.origin, ''));
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Errore nella creazione del pagamento: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    {
      id: 'free',
      name: t('subscription.free'),
      price: '0',
      features: [
        'Accesso catalogo cavalli',
        'Mappa eventi base',
        '5 analisi ROI/mese',
        'Notifiche email'
      ],
      cta: user?.subscription_plan === 'free' ? t('subscription.current') : 'Attiva Gratis',
      disabled: user?.subscription_plan === 'free'
    },
    {
      id: 'premium',
      name: t('subscription.premium'),
      price: '29',
      features: [
        'Tutto Free +',
        'Analisi AI illimitate',
        'Matching sponsor',
        'Certificati blockchain',
        'Alert dazi import',
        'Supporto prioritario'
      ],
      cta: user?.subscription_plan === 'premium' ? t('subscription.current') : t('subscription.subscribe'),
      featured: true,
      disabled: user?.subscription_plan === 'premium'
    },
    {
      id: 'professional',
      name: t('subscription.professional'),
      price: '99',
      features: [
        'Tutto Premium +',
        'API accesso dati',
        'White-label reports',
        'Consulenza dedicata',
        'Gestione aste private',
        'Account manager'
      ],
      cta: user?.subscription_plan === 'professional' ? t('subscription.current') : t('subscription.subscribe'),
      disabled: user?.subscription_plan === 'professional'
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-20 px-6" data-testid="subscription-page">
      <div className="max-w-5xl mx-auto py-12">
        <h1 className="font-cinzel text-4xl font-bold text-center mb-4">{t('subscription.title')}</h1>
        <p className="text-muted-foreground text-center mb-12">Scegli il piano più adatto alle tue esigenze</p>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map(plan => (
            <div 
              key={plan.id}
              className={`bg-card border ${plan.featured ? 'border-primary' : 'border-border'} p-8 relative ${plan.featured ? 'ring-2 ring-primary' : ''}`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-sm font-medium">
                  Consigliato
                </div>
              )}
              
              <h3 className="font-cinzel text-2xl font-bold mb-4">{plan.name}</h3>
              
              <div className="mb-6">
                <span className="text-4xl font-mono font-bold">€{plan.price}</span>
                <span className="text-muted-foreground">{t('subscription.monthly')}</span>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => !plan.disabled && handleSubscribe(plan.id)}
                disabled={plan.disabled || loading}
                className={`w-full py-3 font-medium transition-all ${
                  plan.disabled 
                    ? 'bg-muted text-muted-foreground cursor-not-allowed'
                    : plan.featured
                      ? 'bg-primary text-primary-foreground hover:opacity-90'
                      : 'border border-primary text-primary hover:bg-primary/10'
                }`}
                data-testid={`subscribe-btn-${plan.id}`}
              >
                {loading ? 'Caricamento...' : plan.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Payment Success Page
const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(true);
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');
  const maxAttempts = 10;

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setChecking(false);
        return;
      }

      if (attempts >= maxAttempts) {
        setChecking(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/subscription/status/${sessionId}`);
        setStatus(response.data);
        
        if (response.data.payment_status === 'paid') {
          setChecking(false);
        } else {
          setAttempts(prev => prev + 1);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setAttempts(prev => prev + 1);
      }
    };

    // Poll for status every 2 seconds
    if (checking && attempts < maxAttempts) {
      const timeout = setTimeout(checkStatus, attempts === 0 ? 0 : 2000);
      return () => clearTimeout(timeout);
    }
  }, [sessionId, attempts, checking]);

  return (
    <div className="min-h-screen bg-background pt-20 flex items-center justify-center" data-testid="payment-success-page">
      <div className="bg-card border border-border p-12 text-center max-w-md">
        {checking ? (
          <>
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <p className="text-muted-foreground">Verifica pagamento in corso...</p>
            <p className="text-xs text-muted-foreground mt-2">Tentativo {attempts + 1}/{maxAttempts}</p>
          </>
        ) : status?.payment_status === 'paid' ? (
          <>
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
            <h1 className="font-cinzel text-2xl font-bold mb-4">Pagamento Completato!</h1>
            <p className="text-muted-foreground mb-2">
              Grazie per il tuo abbonamento. Il tuo account è stato aggiornato.
            </p>
            {status.plan_id && (
              <p className="text-primary font-bold mb-6 capitalize">Piano: {status.plan_id}</p>
            )}
            <Link
              to="/dashboard"
              className="inline-block px-8 py-3 bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
            >
              Vai alla Dashboard
            </Link>
          </>
        ) : (
          <>
            <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
            <h1 className="font-cinzel text-2xl font-bold mb-4">Pagamento in Elaborazione</h1>
            <p className="text-muted-foreground mb-6">
              Il pagamento è in fase di elaborazione. Riceverai una conferma via email.
            </p>
            <Link
              to="/subscription"
              className="inline-block px-8 py-3 border border-primary text-primary hover:bg-primary/10 transition-colors"
            >
              Torna agli Abbonamenti
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

// ===== AUTH PAGES =====

// Login Page
const LoginPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20 flex items-center justify-center px-6" data-testid="login-page">
      <div className="bg-card border border-border p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <Horse className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-cinzel text-3xl font-bold">Accedi</h1>
          <p className="text-muted-foreground mt-2">Accedi al tuo account EquiInvest AI</p>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500 p-4 mb-6 flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm text-muted-foreground mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-muted border border-border text-foreground"
              placeholder="tu@email.com"
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="block text-sm text-muted-foreground mb-2">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-muted border border-border text-foreground"
              placeholder="••••••••"
              data-testid="login-password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            data-testid="login-submit"
          >
            {loading ? 'Accesso in corso...' : 'Accedi'}
          </button>
        </form>

        <p className="text-center mt-6 text-muted-foreground">
          Non hai un account?{' '}
          <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
            Registrati
          </Link>
        </p>
      </div>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    user_type: 'scuderia',
    company_name: '',
    phone: '',
    country: 'IT',
    region: 'Toscana',
    vat_number: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }

    if (formData.password.length < 6) {
      setError('La password deve avere almeno 6 caratteri');
      return;
    }

    setLoading(true);

    try {
      await register(formData);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  // Get regions based on selected country in form
  const regions = getRegionsByCountry(formData.country);

  return (
    <div className="min-h-screen bg-background pt-20 flex items-center justify-center px-6 py-12" data-testid="register-page">
      <div className="bg-card border border-border p-8 max-w-2xl w-full">
        <div className="text-center mb-8">
          <Building2 className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-cinzel text-3xl font-bold">Registrati</h1>
          <p className="text-muted-foreground mt-2">Crea il tuo account per vendere all'asta</p>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500 p-4 mb-6 flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Account Type */}
          <div>
            <label className="block text-sm text-muted-foreground mb-2">Tipo Account *</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { value: 'scuderia', label: 'Scuderia', icon: Building2, desc: 'Gestisci cavalli e aste' },
                { value: 'rivenditore', label: 'Rivenditore', icon: Gavel, desc: 'Vendi cavalli' },
                { value: 'sponsor', label: 'Sponsor', icon: Star, desc: 'Sponsorizza cavalli' },
                { value: 'privato', label: 'Privato', icon: User, desc: 'Acquista cavalli' }
              ].map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData({...formData, user_type: type.value})}
                  className={`p-4 border ${formData.user_type === type.value ? 'border-primary bg-primary/10' : 'border-border'} flex flex-col items-center gap-2 text-center`}
                  data-testid={`type-${type.value}`}
                >
                  <type.icon className="w-6 h-6" />
                  <span className="text-sm font-medium">{type.label}</span>
                  <span className="text-xs text-muted-foreground">{type.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Nome/Ragione Sociale *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 bg-muted border border-border text-foreground"
                placeholder="Mario Rossi / Scuderia Bella"
                data-testid="register-name"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Nome Azienda</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                className="w-full px-4 py-3 bg-muted border border-border text-foreground"
                placeholder="Scuderia Bella S.r.l."
                data-testid="register-company"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 bg-muted border border-border text-foreground"
                placeholder="tu@email.com"
                data-testid="register-email"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Telefono</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-3 bg-muted border border-border text-foreground"
                placeholder="+39 123 456 7890"
                data-testid="register-phone"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Paese *</label>
              <select
                value={formData.country}
                onChange={(e) => {
                  const newCountry = e.target.value;
                  const newRegions = getRegionsByCountry(newCountry);
                  setFormData({...formData, country: newCountry, region: newRegions[0]});
                }}
                className="w-full px-4 py-3 bg-muted border border-border text-foreground"
                data-testid="register-country"
              >
                <option value="IT">🇮🇹 Italia</option>
                <option value="CH">🇨🇭 Svizzera</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Regione</label>
              <select
                value={formData.region}
                onChange={(e) => setFormData({...formData, region: e.target.value})}
                className="w-full px-4 py-3 bg-muted border border-border text-foreground"
                data-testid="register-region"
              >
                {regions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-muted-foreground mb-2">Partita IVA</label>
            <input
              type="text"
              value={formData.vat_number}
              onChange={(e) => setFormData({...formData, vat_number: e.target.value})}
              className="w-full px-4 py-3 bg-muted border border-border text-foreground"
              placeholder="IT12345678901"
              data-testid="register-vat"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Password *</label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-4 py-3 bg-muted border border-border text-foreground"
                placeholder="••••••••"
                data-testid="register-password"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Conferma Password *</label>
              <input
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                className="w-full px-4 py-3 bg-muted border border-border text-foreground"
                placeholder="••••••••"
                data-testid="register-confirm"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            data-testid="register-submit"
          >
            {loading ? 'Registrazione in corso...' : 'Crea Account'}
          </button>
        </form>

        <p className="text-center mt-6 text-muted-foreground">
          Hai già un account?{' '}
          <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
            Accedi
          </Link>
        </p>
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const { user, authAxios } = useAuth();
  const [myHorses, setMyHorses] = useState([]);
  const [myAuctions, setMyAuctions] = useState([]);
  const [myShops, setMyShops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [horsesRes, auctionsRes, shopsRes] = await Promise.all([
          authAxios.get('/my/horses'),
          authAxios.get('/my/auctions'),
          authAxios.get('/my/shops')
        ]);
        setMyHorses(horsesRes.data);
        setMyAuctions(auctionsRes.data);
        setMyShops(shopsRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [authAxios]);

  // Redirect sponsors to their dashboard
  if (user?.user_type === 'sponsor') {
    return <Navigate to="/dashboard/sponsor" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-20">
        <Horse className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 px-6" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 className="font-cinzel text-4xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground mt-2">
              Benvenuto, {user?.name} • <span className="capitalize">{user?.user_type}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-3xl">{user?.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
            <span className="text-muted-foreground">{user?.region}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-5 gap-6 mb-12">
          <div className="bg-card border border-border p-6" data-testid="stat-my-horses">
            <Horse className="w-8 h-8 text-primary mb-4" />
            <p className="text-3xl font-mono font-bold text-primary">{myHorses.length}</p>
            <p className="text-sm text-muted-foreground">I Miei Cavalli</p>
          </div>
          <div className="bg-card border border-border p-6" data-testid="stat-my-auctions">
            <Gavel className="w-8 h-8 text-accent mb-4" />
            <p className="text-3xl font-mono font-bold text-accent">{myAuctions.length}</p>
            <p className="text-sm text-muted-foreground">Le Mie Aste</p>
          </div>
          <div className="bg-card border border-border p-6" data-testid="stat-my-shops">
            <Store className="w-8 h-8 text-orange-400 mb-4" />
            <p className="text-3xl font-mono font-bold text-orange-400">{myShops.length}</p>
            <p className="text-sm text-muted-foreground">I Miei Negozi</p>
          </div>
          <div className="bg-card border border-border p-6">
            <TrendingUp className="w-8 h-8 text-green-400 mb-4" />
            <p className="text-3xl font-mono font-bold text-green-400">
              {myAuctions.filter(a => a.status === 'active').length}
            </p>
            <p className="text-sm text-muted-foreground">Aste Attive</p>
          </div>
          <div className="bg-card border border-border p-6">
            <Star className="w-8 h-8 text-yellow-400 mb-4" />
            <p className="text-3xl font-mono font-bold capitalize">{user?.subscription_plan}</p>
            <p className="text-sm text-muted-foreground">Piano Attivo</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <Link
            to="/dashboard/horses"
            className="bg-card border border-border p-6 hover:border-primary transition-colors flex items-center gap-4"
            data-testid="link-my-horses"
          >
            <div className="w-12 h-12 bg-primary/20 flex items-center justify-center">
              <Horse className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-cinzel font-bold text-lg">Gestisci Cavalli</h3>
              <p className="text-sm text-muted-foreground">Aggiungi o modifica cavalli</p>
            </div>
            <ChevronRight className="w-6 h-6 ml-auto text-muted-foreground" />
          </Link>
          <Link
            to="/dashboard/auctions"
            className="bg-card border border-border p-6 hover:border-primary transition-colors flex items-center gap-4"
            data-testid="link-my-auctions"
          >
            <div className="w-12 h-12 bg-accent/20 flex items-center justify-center">
              <Gavel className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h3 className="font-cinzel font-bold text-lg">Gestisci Aste</h3>
              <p className="text-sm text-muted-foreground">Crea o gestisci aste</p>
            </div>
            <ChevronRight className="w-6 h-6 ml-auto text-muted-foreground" />
          </Link>
          <Link
            to="/dashboard/shops"
            className="bg-card border border-border p-6 hover:border-orange-400 transition-colors flex items-center gap-4"
            data-testid="link-my-shops"
          >
            <div className="w-12 h-12 bg-orange-400/20 flex items-center justify-center">
              <Store className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h3 className="font-cinzel font-bold text-lg">Gestisci Negozi</h3>
              <p className="text-sm text-muted-foreground">I tuoi punti vendita</p>
            </div>
            <ChevronRight className="w-6 h-6 ml-auto text-muted-foreground" />
          </Link>
          <Link
            to="/map"
            className="bg-card border border-border p-6 hover:border-green-400 transition-colors flex items-center gap-4"
            data-testid="link-add-events"
          >
            <div className="w-12 h-12 bg-green-400/20 flex items-center justify-center">
              <Map className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h3 className="font-cinzel font-bold text-lg">Aggiungi Eventi</h3>
              <p className="text-sm text-muted-foreground">Crea eventi e classifiche</p>
            </div>
            <ChevronRight className="w-6 h-6 ml-auto text-muted-foreground" />
          </Link>
        </div>

        {/* Recent Horses */}
        {myHorses.length > 0 && (
          <div className="mb-12">
            <h2 className="font-cinzel text-2xl font-bold mb-6">I Miei Cavalli Recenti</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {myHorses.slice(0, 3).map(horse => (
                <div key={horse.id} className="bg-card border border-border p-4">
                  <div className="aspect-[4/3] overflow-hidden mb-4">
                    <img 
                      src={horse.images?.[0] || 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=400'} 
                      alt={horse.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-cinzel font-bold">{horse.name}</h3>
                  <p className="text-sm text-muted-foreground">{horse.breed} • {horse.age} anni</p>
                  <p className="font-mono text-primary mt-2">€{horse.price_eur?.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// My Horses Page
const MyHorsesPage = () => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchHorses = async () => {
    try {
      const response = await authAxios.get('/my/horses');
      setHorses(response.data);
    } catch (error) {
      console.error('Error fetching horses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHorses();
  }, [authAxios]);

  const deleteHorse = async (horseId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo cavallo?')) return;
    
    try {
      await authAxios.delete(`/my/horses/${horseId}`);
      fetchHorses();
    } catch (error) {
      alert('Errore durante l\'eliminazione');
    }
  };

  const toggleSeekingSponsor = async (horseId, seeking) => {
    try {
      await authAxios.put(`/my/horses/${horseId}/seeking-sponsor?seeking=${seeking}`);
      fetchHorses();
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Add Horse Form
  const AddHorseForm = () => {
    const [formData, setFormData] = useState({
      name: '', breed: '', age: '', region: 'Toscana', country: 'IT',
      price_eur: '', fise_id: '', sire: '', dam: '', competitions: '', wins: '', image_url: ''
    });
    const [submitting, setSubmitting] = useState(false);

    // Get regions based on selected country
    const regions = getRegionsByCountry(formData.country);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);

      try {
        await authAxios.post('/my/horses', {
          name: formData.name,
          breed: formData.breed,
          age: parseInt(formData.age),
          region: formData.region,
          country: formData.country,
          price_eur: parseFloat(formData.price_eur),
          fise_id: formData.fise_id || null,
          pedigree: { sire: formData.sire, dam: formData.dam },
          performance_data: { competitions: parseInt(formData.competitions) || 0, wins: parseInt(formData.wins) || 0 },
          images: formData.image_url ? [formData.image_url] : []
        });
        setShowAddForm(false);
        fetchHorses();
      } catch (error) {
        alert('Errore: ' + (error.response?.data?.detail || error.message));
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-card border border-border p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-cinzel text-2xl font-bold">Aggiungi Cavallo</h2>
            <button onClick={() => setShowAddForm(false)} className="p-2 hover:bg-muted">
              <X className="w-6 h-6" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <input type="text" required placeholder="Nome *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2 bg-muted border border-border" />
              <input type="text" required placeholder="Razza *" value={formData.breed} onChange={(e) => setFormData({...formData, breed: e.target.value})} className="w-full px-4 py-2 bg-muted border border-border" />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <input type="number" required min="1" placeholder="Età *" value={formData.age} onChange={(e) => setFormData({...formData, age: e.target.value})} className="w-full px-4 py-2 bg-muted border border-border" />
              <select value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} className="w-full px-4 py-2 bg-muted border border-border">
                <option value="IT">🇮🇹 Italia</option>
                <option value="CH">🇨🇭 Svizzera</option>
              </select>
              <select value={formData.region} onChange={(e) => setFormData({...formData, region: e.target.value})} className="w-full px-4 py-2 bg-muted border border-border">
                {regions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <input type="number" required min="1000" placeholder="Prezzo € *" value={formData.price_eur} onChange={(e) => setFormData({...formData, price_eur: e.target.value})} className="w-full px-4 py-2 bg-muted border border-border" />
              <input type="text" placeholder="FISE ID" value={formData.fise_id} onChange={(e) => setFormData({...formData, fise_id: e.target.value})} className="w-full px-4 py-2 bg-muted border border-border" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <input type="text" placeholder="Padre" value={formData.sire} onChange={(e) => setFormData({...formData, sire: e.target.value})} className="w-full px-4 py-2 bg-muted border border-border" />
              <input type="text" placeholder="Madre" value={formData.dam} onChange={(e) => setFormData({...formData, dam: e.target.value})} className="w-full px-4 py-2 bg-muted border border-border" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <input type="number" min="0" placeholder="Competizioni" value={formData.competitions} onChange={(e) => setFormData({...formData, competitions: e.target.value})} className="w-full px-4 py-2 bg-muted border border-border" />
              <input type="number" min="0" placeholder="Vittorie" value={formData.wins} onChange={(e) => setFormData({...formData, wins: e.target.value})} className="w-full px-4 py-2 bg-muted border border-border" />
            </div>
            <input type="url" placeholder="URL Immagine" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="w-full px-4 py-2 bg-muted border border-border" />
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowAddForm(false)} className="flex-1 py-3 border border-border text-foreground hover:bg-muted transition-colors">
                Annulla
              </button>
              <button type="submit" disabled={submitting} className="flex-1 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50">
                {submitting ? 'Salvando...' : 'Aggiungi Cavallo'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pt-20 px-6" data-testid="my-horses-page">
      <div className="max-w-7xl mx-auto py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-cinzel text-4xl font-bold">I Miei Cavalli</h1>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-6 py-3 bg-primary text-primary-foreground font-medium flex items-center gap-2"
            data-testid="add-horse-btn"
          >
            <Plus className="w-5 h-5" />
            Aggiungi Cavallo
          </button>
        </div>

        {showAddForm && <AddHorseForm />}

        {loading ? (
          <div className="text-center py-20"><Horse className="w-12 h-12 text-primary animate-pulse mx-auto" /></div>
        ) : horses.length === 0 ? (
          <div className="text-center py-20 bg-card border border-border">
            <Horse className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-cinzel text-xl font-bold mb-2">Nessun Cavallo</h3>
            <p className="text-muted-foreground">Aggiungi il tuo primo cavallo per iniziare a vendere</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {horses.map(horse => (
              <div key={horse.id} className="bg-card border border-border overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={horse.images?.[0] || 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=400'} alt={horse.name} className="w-full h-full object-cover" />
                  {horse.seeking_sponsor && (
                    <div className="absolute top-2 right-2 bg-accent px-2 py-1 text-xs text-white flex items-center gap-1">
                      <Star className="w-3 h-3" /> Cerca Sponsor
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-cinzel font-bold text-lg">{horse.name}</h3>
                  <p className="text-sm text-muted-foreground">{horse.breed} • {horse.age} anni</p>
                  <p className="font-mono text-primary mt-2 text-xl">€{horse.price_eur?.toLocaleString()}</p>
                  <p className="text-sm text-accent">ROI: {horse.roi_prediction?.toFixed(1)}%</p>
                  
                  {/* Toggle Cerca Sponsor */}
                  <div className="mt-3 pt-3 border-t border-border">
                    <button
                      onClick={() => toggleSeekingSponsor(horse.id, !horse.seeking_sponsor)}
                      className={`w-full px-3 py-2 text-sm flex items-center justify-center gap-2 ${
                        horse.seeking_sponsor 
                          ? 'bg-accent/20 text-accent border border-accent' 
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                      data-testid={`toggle-sponsor-${horse.id}`}
                    >
                      <Star className="w-4 h-4" />
                      {horse.seeking_sponsor ? 'Ricerca Sponsor Attiva' : 'Cerca Sponsor'}
                    </button>
                  </div>
                  
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => navigate(`/horses/${horse.id}`)} className="flex-1 px-3 py-2 bg-muted text-sm">Dettagli</button>
                    <button onClick={() => navigate('/sponsors?my=true')} className="flex-1 px-3 py-2 bg-primary/20 text-primary text-sm" title="Trova sponsor">
                      <Users className="w-4 h-4 inline mr-1" />Sponsor
                    </button>
                    <button onClick={() => deleteHorse(horse.id)} className="px-3 py-2 bg-red-500/20 text-red-400" data-testid={`delete-horse-${horse.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// My Auctions Page
const MyAuctionsPage = () => {
  const { authAxios } = useAuth();
  const [auctions, setAuctions] = useState([]);
  const [myHorses, setMyHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const fetchData = async () => {
    try {
      const [auctionsRes, horsesRes] = await Promise.all([
        authAxios.get('/my/auctions'),
        authAxios.get('/my/horses')
      ]);
      setAuctions(auctionsRes.data);
      setMyHorses(horsesRes.data);
    } catch (error) {
      console.error('Error fetching auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [authAxios]);

  // Create Auction Form
  const CreateAuctionForm = () => {
    const [formData, setFormData] = useState({ horse_id: '', start_price: '', end_date: '' });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);

      try {
        await authAxios.post('/my/auctions', {
          horse_id: formData.horse_id,
          start_price: parseFloat(formData.start_price),
          end_date: formData.end_date
        });
        setShowCreateForm(false);
        fetchData();
      } catch (error) {
        alert('Errore: ' + (error.response?.data?.detail || error.message));
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-card border border-border p-8 max-w-md w-full">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-cinzel text-2xl font-bold">Crea Asta</h2>
            <button onClick={() => setShowCreateForm(false)} className="p-2 hover:bg-muted"><X className="w-6 h-6" /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Seleziona Cavallo *</label>
              <select required value={formData.horse_id} onChange={(e) => setFormData({...formData, horse_id: e.target.value})} className="w-full px-4 py-3 bg-muted border border-border">
                <option value="">-- Seleziona --</option>
                {myHorses.map(h => <option key={h.id} value={h.id}>{h.name} (€{h.price_eur?.toLocaleString()})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Prezzo Base (€) *</label>
              <input type="number" required min="1000" value={formData.start_price} onChange={(e) => setFormData({...formData, start_price: e.target.value})} className="w-full px-4 py-3 bg-muted border border-border" />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Data Fine Asta *</label>
              <input type="date" required value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} className="w-full px-4 py-3 bg-muted border border-border" />
            </div>
            <p className="text-xs text-muted-foreground">Commissione: 7% sul prezzo finale</p>
            <button type="submit" disabled={submitting} className="w-full py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50">
              {submitting ? 'Creando...' : 'Crea Asta'}
            </button>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pt-20 px-6" data-testid="my-auctions-page">
      <div className="max-w-7xl mx-auto py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-cinzel text-4xl font-bold">Le Mie Aste</h1>
          <button
            onClick={() => setShowCreateForm(true)}
            disabled={myHorses.length === 0}
            className="px-6 py-3 bg-primary text-primary-foreground font-medium flex items-center gap-2 disabled:opacity-50"
            data-testid="create-auction-btn"
          >
            <Plus className="w-5 h-5" />
            Crea Asta
          </button>
        </div>

        {myHorses.length === 0 && (
          <div className="bg-yellow-500/20 border border-yellow-500 p-4 mb-8 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            <span>Devi prima aggiungere almeno un cavallo per creare un'asta</span>
          </div>
        )}

        {showCreateForm && <CreateAuctionForm />}

        {loading ? (
          <div className="text-center py-20"><Gavel className="w-12 h-12 text-primary animate-pulse mx-auto" /></div>
        ) : auctions.length === 0 ? (
          <div className="text-center py-20 bg-card border border-border">
            <Gavel className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-cinzel text-xl font-bold mb-2">Nessuna Asta</h3>
            <p className="text-muted-foreground">Crea la tua prima asta per iniziare a vendere</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {auctions.map(auction => (
              <div key={auction.id} className="bg-card border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className={`px-3 py-1 text-sm ${auction.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-muted'}`}>
                    {auction.status === 'active' ? 'Attiva' : auction.status}
                  </span>
                  <span className="text-xs text-muted-foreground">7% comm.</span>
                </div>
                <h3 className="font-cinzel font-bold text-lg mb-2">{auction.horse_name}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Base:</span><span className="font-mono">€{auction.start_price?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Attuale:</span><span className="font-mono text-primary font-bold">€{auction.current_bid?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Offerte:</span><span>{auction.bids?.length || 0}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Scadenza:</span><span>{auction.end_date}</span></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// My Shops Page
const MyShopsPage = () => {
  const { authAxios } = useAuth();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingShop, setEditingShop] = useState(null);

  const shopCategories = [
    { id: 'selleria', name: 'Selleria', icon: '🐎' },
    { id: 'mangimi', name: 'Mangimi', icon: '🌾' },
    { id: 'veterinario', name: 'Veterinario', icon: '🩺' },
    { id: 'maniscalco', name: 'Maniscalco', icon: '🔨' },
    { id: 'trasporto', name: 'Trasporto', icon: '🚚' },
    { id: 'abbigliamento', name: 'Abbigliamento', icon: '👕' },
    { id: 'attrezzature', name: 'Attrezzature', icon: '⚙️' },
    { id: 'altro', name: 'Altro', icon: '📦' }
  ];

  const fetchShops = async () => {
    try {
      const response = await authAxios.get('/my/shops');
      setShops(response.data);
    } catch (error) {
      console.error('Error fetching shops:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
  }, [authAxios]);

  const deleteShop = async (shopId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo negozio?')) return;
    
    try {
      await authAxios.delete(`/my/shops/${shopId}`);
      fetchShops();
    } catch (error) {
      alert('Errore durante l\'eliminazione');
    }
  };

  // Shop Form Component (for Add/Edit)
  const ShopForm = ({ shop = null, onClose }) => {
    const [formData, setFormData] = useState({
      name: shop?.name || '',
      category: shop?.category || 'selleria',
      description: shop?.description || '',
      address: shop?.address || '',
      city: shop?.city || '',
      region: shop?.region || 'Toscana',
      country: shop?.country || 'IT',
      lat: shop?.lat || 45.5,
      lng: shop?.lng || 9.5,
      phone: shop?.phone || '',
      email: shop?.email || '',
      website: shop?.website || '',
      opening_hours: shop?.opening_hours || ''
    });
    const [submitting, setSubmitting] = useState(false);

    const regions = getRegionsByCountry(formData.country);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      try {
        if (shop) {
          await authAxios.put(`/my/shops/${shop.id}`, formData);
          alert('Negozio aggiornato con successo!');
        } else {
          await authAxios.post('/shops', formData);
          alert('Negozio aggiunto con successo!');
        }
        onClose();
        fetchShops();
      } catch (error) {
        alert('Errore: ' + (error.response?.data?.detail || error.message));
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
        <div className="bg-card border border-border p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-cinzel text-xl font-bold">
              {shop ? 'Modifica Negozio' : 'Aggiungi Negozio'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-muted">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground block mb-1">Nome Negozio *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
                placeholder="es. Selleria Rossi"
              />
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground block mb-1">Categoria</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
              >
                {shopCategories.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Paese</label>
                <select
                  value={formData.country}
                  onChange={(e) => {
                    const newCountry = e.target.value;
                    const newRegions = getRegionsByCountry(newCountry);
                    setFormData({...formData, country: newCountry, region: newRegions[0]});
                  }}
                  className="w-full px-3 py-2 bg-muted border border-border"
                >
                  <option value="IT">🇮🇹 Italia</option>
                  <option value="CH">🇨🇭 Svizzera</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Regione</label>
                <select
                  value={formData.region}
                  onChange={(e) => setFormData({...formData, region: e.target.value})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                >
                  {regions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Città *</label>
              <input
                type="text"
                required
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
                placeholder="es. Firenze"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Indirizzo *</label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
                placeholder="Via Roma, 15"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Latitudine</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.lat}
                  onChange={(e) => setFormData({...formData, lat: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Longitudine</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.lng}
                  onChange={(e) => setFormData({...formData, lng: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Telefono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                  placeholder="+39 055 123456"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground block mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 bg-muted border border-border"
                  placeholder="info@negozio.it"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Sito Web</label>
              <input
                type="text"
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
                placeholder="www.negozio.it"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Orari Apertura</label>
              <input
                type="text"
                value={formData.opening_hours}
                onChange={(e) => setFormData({...formData, opening_hours: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border"
                placeholder="Lun-Sab 9:00-19:00"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1">Descrizione</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 bg-muted border border-border h-20"
                placeholder="Descrivi il tuo negozio..."
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 border border-border text-foreground hover:bg-muted"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Salvando...' : (shop ? 'Salva Modifiche' : 'Aggiungi Negozio')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pt-20 px-6" data-testid="my-shops-page">
      <div className="max-w-7xl mx-auto py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2">
              <ChevronLeft className="w-4 h-4" /> Dashboard
            </Link>
            <h1 className="font-cinzel text-3xl font-bold">I Miei Negozi</h1>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-primary text-primary-foreground px-6 py-3 font-medium hover:opacity-90 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Aggiungi Negozio
          </button>
        </div>

        {showAddForm && <ShopForm onClose={() => setShowAddForm(false)} />}
        {editingShop && <ShopForm shop={editingShop} onClose={() => setEditingShop(null)} />}

        {loading ? (
          <div className="text-center py-20"><Store className="w-12 h-12 text-primary animate-pulse mx-auto" /></div>
        ) : shops.length === 0 ? (
          <div className="text-center py-20 bg-card border border-border">
            <Store className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-cinzel text-xl font-bold mb-2">Nessun Negozio</h3>
            <p className="text-muted-foreground mb-6">Aggiungi il tuo primo negozio per farlo apparire sulla mappa</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-primary text-primary-foreground px-6 py-3 font-medium hover:opacity-90 inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Aggiungi Negozio
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shops.map(shop => (
              <div key={shop.id} className="bg-card border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{shopCategories.find(c => c.id === shop.category)?.icon || '📦'}</span>
                    <span className="px-2 py-1 bg-muted text-xs">{shopCategories.find(c => c.id === shop.category)?.name}</span>
                  </div>
                  <span>{shop.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
                </div>
                
                <h3 className="font-cinzel font-bold text-lg mb-2">{shop.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{shop.city}, {shop.region}</p>
                
                {shop.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{shop.description}</p>
                )}
                
                <div className="space-y-1 text-sm mb-4">
                  {shop.address && <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" /> {shop.address}</p>}
                  {shop.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" /> {shop.phone}</p>}
                  {shop.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground" /> {shop.email}</p>}
                </div>
                
                <div className="flex gap-2 pt-4 border-t border-border">
                  <button
                    onClick={() => setEditingShop(shop)}
                    className="flex-1 py-2 bg-muted text-foreground text-sm hover:bg-muted/80 flex items-center justify-center gap-2"
                  >
                    <Edit className="w-4 h-4" /> Modifica
                  </button>
                  <button
                    onClick={() => deleteShop(shop.id)}
                    className="flex-1 py-2 bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Elimina
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Sponsor Dashboard Page
const SponsorDashboardPage = () => {
  const { user, authAxios } = useAuth();
  const navigate = useNavigate();
  const [sponsorProfile, setSponsorProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const fetchProfile = async () => {
    try {
      const response = await authAxios.get('/my/sponsor');
      setSponsorProfile(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setSponsorProfile(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.user_type !== 'sponsor') {
      navigate('/dashboard');
      return;
    }
    fetchProfile();
  }, [user, navigate, authAxios]);

  const deactivateProfile = async () => {
    if (!window.confirm('Sei sicuro di voler disattivare il tuo profilo sponsor? Non sarai più visibile agli altri utenti.')) return;
    
    try {
      await authAxios.delete('/my/sponsor');
      fetchProfile();
      alert('Profilo sponsor disattivato');
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || error.message));
    }
  };

  const reactivateProfile = async () => {
    try {
      await authAxios.post('/my/sponsor/reactivate');
      fetchProfile();
      alert('Profilo sponsor riattivato');
    } catch (error) {
      alert('Errore: ' + (error.response?.data?.detail || error.message));
    }
  };

  const sponsorTypes = [
    { value: 'feed', label: 'Mangimi', icon: '🌾' },
    { value: 'equipment', label: 'Attrezzatura', icon: '🎯' },
    { value: 'transport', label: 'Trasporto', icon: '🚚' },
    { value: 'training', label: 'Addestramento', icon: '🏆' },
    { value: 'veterinary', label: 'Veterinario', icon: '⚕️' },
    { value: 'insurance', label: 'Assicurazione', icon: '🛡️' },
  ];

  // Create Profile Form
  const CreateProfileForm = () => {
    const [formData, setFormData] = useState({
      name: user?.company_name || user?.name || '',
      type: 'equipment',
      description: '',
      website: '',
      logo_url: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);

      try {
        await authAxios.post('/my/sponsor', formData);
        setShowCreateForm(false);
        fetchProfile();
      } catch (error) {
        alert('Errore: ' + (error.response?.data?.detail || error.message));
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-card border border-border p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-cinzel text-2xl font-bold">Crea Profilo Sponsor</h2>
            <button onClick={() => setShowCreateForm(false)} className="p-2 hover:bg-muted"><X className="w-6 h-6" /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Nome Azienda *</label>
              <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 bg-muted border border-border" placeholder="Nome della tua azienda" />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Tipo di Sponsor *</label>
              <div className="grid grid-cols-3 gap-2">
                {sponsorTypes.map(t => (
                  <button key={t.value} type="button" onClick={() => setFormData({...formData, type: t.value})}
                    className={`p-3 border ${formData.type === t.value ? 'border-primary bg-primary/10' : 'border-border'} flex flex-col items-center gap-1`}>
                    <span className="text-2xl">{t.icon}</span>
                    <span className="text-xs">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Descrizione</label>
              <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-3 bg-muted border border-border h-24 resize-none" placeholder="Descrivi la tua attività..." />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Sito Web</label>
              <input type="url" value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} className="w-full px-4 py-3 bg-muted border border-border" placeholder="https://www.tuosito.com" />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">URL Logo</label>
              <input type="url" value={formData.logo_url} onChange={(e) => setFormData({...formData, logo_url: e.target.value})} className="w-full px-4 py-3 bg-muted border border-border" placeholder="https://..." />
            </div>
            <button type="submit" disabled={submitting} className="w-full py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50">
              {submitting ? 'Creando...' : 'Crea Profilo Sponsor'}
            </button>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-20">
        <Star className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 px-6" data-testid="sponsor-dashboard-page">
      <div className="max-w-4xl mx-auto py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-cinzel text-4xl font-bold">Dashboard Sponsor</h1>
            <p className="text-muted-foreground mt-2">Benvenuto, {user?.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-3xl">{user?.country === 'IT' ? '🇮🇹' : '🇨🇭'}</span>
          </div>
        </div>

        {showCreateForm && <CreateProfileForm />}

        {!sponsorProfile ? (
          <div className="bg-card border border-border p-12 text-center">
            <Star className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-cinzel text-2xl font-bold mb-4">Crea il tuo Profilo Sponsor</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Crea il tuo profilo per essere visibile ai proprietari di cavalli e ricevere richieste di sponsorizzazione cross-border IT/CH.
            </p>
            <button onClick={() => setShowCreateForm(true)} className="px-8 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90" data-testid="create-sponsor-btn">
              <Star className="w-5 h-5 inline mr-2" />
              Crea Profilo Sponsor
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Profile Status */}
            <div className={`p-4 border ${sponsorProfile.is_active !== false ? 'border-green-500 bg-green-500/10' : 'border-red-500 bg-red-500/10'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {sponsorProfile.is_active !== false ? (
                    <><CheckCircle className="w-5 h-5 text-green-400" /><span className="text-green-400 font-medium">Profilo Attivo</span></>
                  ) : (
                    <><AlertTriangle className="w-5 h-5 text-red-400" /><span className="text-red-400 font-medium">Profilo Disattivato</span></>
                  )}
                </div>
                {sponsorProfile.is_active === false ? (
                  <button onClick={reactivateProfile} className="px-4 py-2 bg-green-500 text-white text-sm hover:opacity-90">Riattiva</button>
                ) : (
                  <button onClick={deactivateProfile} className="px-4 py-2 bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30">Disattiva</button>
                )}
              </div>
            </div>

            {/* Profile Card */}
            <div className="bg-card border border-border p-6">
              <div className="flex items-start gap-6">
                {sponsorProfile.logo_url ? (
                  <img src={sponsorProfile.logo_url} alt="Logo" className="w-24 h-24 object-cover border border-border" />
                ) : (
                  <div className="w-24 h-24 bg-muted flex items-center justify-center">
                    <Star className="w-10 h-10 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="font-cinzel text-2xl font-bold">{sponsorProfile.name}</h2>
                    <span className="text-2xl">{sponsorTypes.find(t => t.value === sponsorProfile.type)?.icon}</span>
                  </div>
                  <p className="text-muted-foreground mb-4">{sponsorProfile.description || 'Nessuna descrizione'}</p>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div><span className="text-muted-foreground">Tipo:</span> <span className="capitalize">{sponsorProfile.type}</span></div>
                    <div><span className="text-muted-foreground">Paese:</span> {sponsorProfile.country === 'IT' ? '🇮🇹 Italia' : '🇨🇭 Svizzera'}</div>
                    <div><span className="text-muted-foreground">Email:</span> {sponsorProfile.contact_email}</div>
                    {sponsorProfile.website && <div><span className="text-muted-foreground">Sito:</span> <a href={sponsorProfile.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{sponsorProfile.website}</a></div>}
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-muted border border-border p-6">
              <h3 className="font-cinzel font-bold mb-4">Come funziona lo Sponsor Matching?</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• I proprietari di cavalli possono cercare sponsor per i loro animali</li>
                <li>• Il sistema abbina sponsor e cavalli in base a regione, performance e genetica</li>
                <li>• Gli abbinamenti cross-border (IT↔CH) includono alert su dazi doganali</li>
                <li>• Più alta è la compatibilità, più visibilità avrai</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Public Certificate Verification Page
const VerifyCertificatePage = () => {
  const { hash } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verify = async () => {
      try {
        const response = await axios.get(`${API}/certificates/verify/${hash}`);
        setResult(response.data);
      } catch {
        setResult({ valid: false });
      } finally {
        setLoading(false);
      }
    };
    if (hash) verify();
  }, [hash]);

  const cert = result?.certificate;

  return (
    <div className="min-h-screen bg-background pt-20 px-6" data-testid="verify-page">
      <div className="max-w-2xl mx-auto py-12">
        <h1 className="font-cinzel text-3xl font-bold mb-2 text-center">Verifica Certificato</h1>
        <p className="text-muted-foreground text-center mb-8">Verifica autenticita tramite blockchain Bitcoin</p>

        {loading ? (
          <div className="text-center py-16">
            <Shield className="w-16 h-16 text-primary animate-pulse mx-auto" />
            <p className="mt-4 text-muted-foreground">Verifica in corso...</p>
          </div>
        ) : result?.valid && cert ? (
          <div className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 p-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-green-400 mb-1">Certificato Autentico</h2>
              {cert.certificate_number && <p className="font-mono text-sm text-muted-foreground">{cert.certificate_number}</p>}
            </div>

            <div className="bg-card border border-border p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground block">Cavallo</span><span className="font-bold">{cert.horse_name}</span></div>
                <div><span className="text-muted-foreground block">Tipo</span><span className="font-medium">{cert.certificate_type === 'health' ? 'Sanitario UE' : cert.certificate_type === 'export' ? 'Export IT/CH' : cert.certificate_type === 'competition' ? 'FISE/FEI' : 'Passaporto'}</span></div>
                <div><span className="text-muted-foreground block">Emissione</span>{cert.issue_date}</div>
                <div><span className="text-muted-foreground block">Scadenza</span>{cert.expiry_date}</div>
                {cert.microchip_number && <div><span className="text-muted-foreground block">Microchip</span><span className="font-mono text-xs">{cert.microchip_number}</span></div>}
                {cert.fise_id && <div><span className="text-muted-foreground block">FISE ID</span><span className="font-mono text-xs">{cert.fise_id}</span></div>}
                {cert.fei_id && <div><span className="text-muted-foreground block">FEI ID</span><span className="font-mono text-xs">{cert.fei_id}</span></div>}
                <div><span className="text-muted-foreground block">Stato Sanitario</span><span className={`font-bold ${cert.health_status === 'Idoneo' ? 'text-green-400' : 'text-yellow-400'}`}>{cert.health_status || 'N/A'}</span></div>
              </div>
              
              <div className="border-t border-border pt-4">
                <h3 className="font-bold text-primary text-sm mb-2">Blockchain Bitcoin</h3>
                <p className="font-mono text-xs break-all text-muted-foreground">{cert.blockchain_hash}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-0.5 text-xs ${cert.ots_status === 'confirmed' ? 'bg-green-500/20 text-green-400' : cert.ots_status === 'pending' ? 'bg-orange-500/20 text-orange-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {cert.ots_status === 'confirmed' ? 'Confermato su Bitcoin' : cert.ots_status === 'pending' ? 'In attesa conferma' : 'Locale'}
                  </span>
                </div>
                {cert.ots_verify_url && (
                  <a href={cert.ots_verify_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-orange-400 hover:text-orange-300 mt-3 text-sm">
                    <Shield className="w-4 h-4" /> Verifica su OpenTimestamps.org
                  </a>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-red-500/10 border border-red-500/30 p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-red-400 mb-2">Certificato Non Trovato</h2>
            <p className="text-muted-foreground text-sm">L'hash fornito non corrisponde a nessun certificato registrato.</p>
          </div>
        )}

        <div className="text-center mt-8">
          <Link to="/certificates" className="text-primary hover:underline text-sm">Torna ai Certificati</Link>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App dark">
      <AuthProvider>
        <BrowserRouter>
          <Navigation />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/horses" element={<HorsesPage />} />
            <Route path="/horses/:horseId" element={<HorseDetailPage />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/shops" element={<ShopsPage />} />
            <Route path="/auctions" element={<AuctionsPage />} />
            <Route path="/certificates" element={<CertificatesPage />} />
            <Route path="/verify/:hash" element={<VerifyCertificatePage />} />
            <Route path="/sponsors" element={<SponsorsPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/dashboard/horses" element={<ProtectedRoute><MyHorsesPage /></ProtectedRoute>} />
            <Route path="/dashboard/auctions" element={<ProtectedRoute><MyAuctionsPage /></ProtectedRoute>} />
            <Route path="/dashboard/shops" element={<ProtectedRoute><MyShopsPage /></ProtectedRoute>} />
            <Route path="/dashboard/sponsor" element={<ProtectedRoute><SponsorDashboardPage /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
